import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import InvoiceLinkHistoryPanel from './InvoiceLinkHistoryPanel';
import axios from 'axios';
import Layout from './Layout';
import * as XLSX from 'xlsx';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Space,
    message,
    Popconfirm,
    Tag,
    Row,
    Col,
    Switch,
    InputNumber,
    DatePicker,
    Collapse,
    Alert,
    Typography,
    notification,
    Tooltip,
    Radio
} from 'antd';
import {
    SyncOutlined,
    DollarCircleOutlined,
    PlusOutlined,
    CopyOutlined,
    SaveOutlined,
    CloseOutlined,
    EditOutlined,
    DeleteOutlined,
    ClearOutlined,
    HistoryOutlined,
    DownOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
    useLocation,
    useNavigate
} from 'react-router-dom';
import {
    formatPercent,
    parsePercent,
    percentInputNumberProps
} from '../utils/formatPercent';

const { TextArea } = Input;
const { Panel } = Collapse;
const { RangePicker } = DatePicker;
const { Text } = Typography;


const TASK_STATUS_OPTIONS = [
    { value: 'new', label: 'Новая' },
    { value: 'in_progress', label: 'В работе' },
    { value: 'filled', label: 'Заполнена' },
    { value: 'rework', label: 'На доработке' },
    { value: 'approval', label: 'Согласование' },
    { value: 'analysis', label: 'Требует анализа' },
    { value: 'approved', label: 'Утверждено' }
];

const normalizeWorkCode = value => {
    const text = String(value || '').trim();
    const match = text.match(/^(\d+(?:\.\d+)*)\.?/);

    if (!match) {
        return '';
    }

    return `${match[1]}.`;
};

const extractWorkCodeFromRate = value => {
    const text = String(value || '').trim();
    const match = text.match(/^(\d+(?:\.\d+)*\.?)/);

    if (!match) {
        return '';
    }

    return normalizeWorkCode(match[1]);
};

// --- ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ---

const mathRound = (num) => {
    return Math.round((num + Number.EPSILON) * 100) / 100;
};

// ActiveMap отдаёт дробные значения с запятой («5,1»), parseFloat отрезал бы десятые
const toNumber = (value) => {
    if (typeof value === 'number') return value;
    if (value === null || value === undefined) return NaN;
    return parseFloat(String(value).replace(/\s/g, '').replace(',', '.'));
};

const safeParseIntArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data.map(item => parseInt(item, 10)).filter(n => !isNaN(n));
    if (typeof data === 'string') {
        try {
            const parsed = JSON.parse(data);
            if (Array.isArray(parsed)) return parsed.map(item => parseInt(item, 10)).filter(n => !isNaN(n));
        } catch (e) {
            return data.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
        }
    }
    return [];
};

/** Регион заказчика. По нему выбирается прайс исполнителя, если их несколько. */
const regionKeyFromCustomer = name => {
    const s = String(name || '').toLowerCase().replace(/ё/g, 'е');
    if (!s) return null;
    if (s.includes('северо-запад') || s.includes('северо запад')) return 'сз';
    if (s.includes('северо-восток') || s.includes('северо восток')) return 'св';
    if (s.includes('центр')) return 'центр';
    if (s.includes('новгород')) return 'вн';
    if (s.includes('екатеринбург')) return 'екб';
    if (s.includes('пермь')) return 'прм';
    if (s.includes('самара')) return 'сам';
    return null;
};

const REGION_ALIASES = {
    сз: ['сз', 'северо-запад', 'северо запад'],
    св: ['св', 'северо-восток', 'северо восток'],
    центр: ['центр'],
    вн: ['вн', 'новгород'],
    екб: ['екб', 'екатеринбург'],
    прм: ['прм', 'пермь'],
    сам: ['сам', 'самара']
};

const priceListMatchesToken = (priceName, token) => {
    const escaped = String(token).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|[^a-zа-яё])${escaped}([^a-zа-яё]|$)`, 'i').test(String(priceName || ''));
};

/**
 * Чем выше, тем ближе прайс к заказчику.
 * «СЗ (МО)» проигрывает обычному «СЗ», если в заказчике нет этой пометки.
 */
const scorePriceList = (priceName, regionKey, customerName) => {
    if (!regionKey) return 0;
    const aliases = REGION_ALIASES[regionKey] || [regionKey];
    if (!aliases.some(token => priceListMatchesToken(priceName, token))) return 0;
    let score = 10;
    const customer = String(customerName || '').toLowerCase().replace(/ё/g, 'е');
    const extras = String(priceName || '').match(/\(([^)]+)\)/g) || [];
    extras.forEach(raw => {
        const extra = raw.slice(1, -1).trim().toLowerCase().replace(/ё/g, 'е');
        if (extra && !priceListMatchesToken(customer, extra)) score -= 5;
    });
    return score;
};

/** Цена номенклатуры из прайса исполнителя, совпадающего с заказчиком. */
const pickNomenclatureRate = (nomenclature, counterparty, customerName, priceTypes) => {
    const allowed = safeParseIntArray(counterparty?.price_lists);
    const region = regionKeyFromCustomer(customerName);
    const inOrder = [];
    Object.entries(nomenclature?.prices || {}).forEach(([ptidStr, val]) => {
        const ptid = parseInt(ptidStr, 10);
        if (!allowed.includes(ptid) || typeof val !== 'number' || !(val > 0)) return;
        const ptName = (priceTypes || []).find(pt => pt.id === ptid)?.name || '';
        inOrder.push({
            priceValue: val,
            unit: nomenclature.unit,
            ptName,
            score: scorePriceList(ptName, region, customerName)
        });
    });
    if (!inOrder.length) return null;
    const ranked = inOrder
        .map((item, index) => ({ ...item, index }))
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score || a.index - b.index);
    return ranked[0] || inOrder[0];
};

const parseCustomFields = taskData => {
    const raw = taskData?.custom_fields;
    if (!raw) return {};
    if (typeof raw === 'string') return JSON.parse(raw);
    return raw;
};

const taskViewFromRecord = record => {
    let raw = record?.raw_data;
    if (typeof raw === 'string') {
        try {
            raw = JSON.parse(raw);
        } catch (_) {
            raw = {};
        }
    }
    if (!raw || typeof raw !== 'object') raw = {};
    return {
        ...raw,
        news_date: raw.news_date || record?.news_date || null
    };
};

const checkDateTimeCondition = async (dateToCheck, condition) => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.post('/api/tasks/check-date-condition',
            { dateToCheck, condition },
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        return response.data.isMatch;
    } catch (error) {
        console.error("Ошибка при проверке условия даты:", error);
        return false;
    }
};

const calculateTemplateVariables = (taskData, customFields, counterparty, getDynamicValue) => {
    /*const roadToMinutes = toNumber(getDynamicValue('<Doroga_do_obiekta_(minut)>', taskData, customFields)) || 0;
    const roadFromMinutes = toNumber(getDynamicValue('<Doroga_s_obiekta_(minut)>', taskData, customFields)) || 0;
    const workMinut = toNumber(getDynamicValue('<Vremja_na_rabote>', taskData, customFields)) || 0;
    const workTimeHours = Math.round((workMinut / 60) * 10) / 10;
    const mileageKm = toNumber(getDynamicValue('<Probeg,_km>', taskData, customFields)) || 0;
    const dieselMinutes = toNumber(getDynamicValue('<CHase_dizelenija>', taskData, customFields)) || 0;

    const fuelConsumption = parseFloat(counterparty?.fuel_consumption) || 0;

    const travelTimeHours = Math.round(((roadToMinutes + roadFromMinutes) / 60) * 10) / 10;
   // const totalTimeHours = workTimeHours + travelTimeHours;
    const totalTimeHours = Math.round(((roadToMinutes + roadFromMinutes + workMinut) / 60) * 10) / 10;
    const fuelSpentLiters = (mileageKm * fuelConsumption) / 100;
    const dieselHoursRounded = Math.round((dieselMinutes / 60) * 10) / 10;
    const dieselHoursMinusOne = Math.max(dieselHoursRounded - 1, 0);
    const generation = Math.round(((dieselMinutes + travelTimeHours)/ 60) * 10) / 10;*/
    const roadToMinutes = toNumber(getDynamicValue('<Doroga_do_obiekta_(minut)>', taskData, customFields)) || 0;
    const roadFromMinutes = toNumber(getDynamicValue('<Doroga_s_obiekta_(minut)>', taskData, customFields)) || 0;
    const workMinut = toNumber(getDynamicValue('<Vremja_na_rabote>', taskData, customFields)) || 0;

    // Округление времени работы
    const workTimeHours = Math.round((workMinut / 60) * 100) / 100;
    
    const mileageKm = toNumber(getDynamicValue('<Probeg,_km>', taskData, customFields)) || 0;
    const dieselMinutes = toNumber(getDynamicValue('<CHase_dizelenija>', taskData, customFields)) || 0;
    const fuelConsumption = parseFloat(counterparty?.fuel_consumption) || 0;

    // Округление времени в дороге
    const travelTimeHours = Math.round(((roadToMinutes + roadFromMinutes) / 60) * 100) / 100;

    // Общее время (дорога + работа)
    const totalTimeHours = Math.round(((roadToMinutes + roadFromMinutes + workMinut) / 60) * 100) / 100;

    // Затрачено топлива
    const fuelSpentLiters = Math.round(((mileageKm * fuelConsumption) / 100) * 100) / 100;

    // Время дизеления
    const dieselHoursRounded = Math.round((dieselMinutes / 60) * 100) / 100;
    const dieselHoursMinusOne = Math.max(Math.round((dieselHoursRounded - 1) * 100) / 100, 0);

    // Генерация (дизеление + дорога)
    // Внимание: если travelTimeHours уже в часах, не делим его на 60
    const generation = Math.round(((dieselMinutes / 60) + travelTimeHours) * 100) / 100;


    return {
        'время_в_дороге': travelTimeHours,
        'общее_время': totalTimeHours,
        'затрачено_топлива_на_авто': fuelSpentLiters,
        'время_дизеления': dieselHoursRounded,
        'время_дизеления_минус1': dieselHoursMinusOne,
        'генерация': generation,
    };
};

const resolveTemplateValue = (placeholder, taskData, customFields, counterparty, calculatedVars, getDynamicValueFunc) => {
    if (typeof placeholder !== 'string') return placeholder;
    const trimmed = placeholder.trim();

    if (trimmed.startsWith('$') && trimmed.endsWith('$')) {
        const varName = trimmed.slice(1, -1);
        return calculatedVars[varName] !== undefined ? parseFloat(calculatedVars[varName]) : 0;
    }
    if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
        const field = trimmed.slice(2, -2);
        return counterparty?.[field] !== undefined ? counterparty[field] : null;
    }
    if (trimmed.startsWith('<') && trimmed.endsWith('>')) {
        return getDynamicValueFunc(trimmed, taskData, customFields);
    }
    if (!isNaN(parseFloat(trimmed)) && isFinite(parseFloat(trimmed))) {
        return parseFloat(trimmed);
    }
    return placeholder;
};

const _getCounterpartyOptionByName = (name, allCounterparties) => {
    if (!name || !Array.isArray(allCounterparties)) return null;
    const found = allCounterparties.find(c => c.name.toLowerCase() === name.toLowerCase());
    return found ? { value: found.id, label: found.name } : null;
};

const _getArticleOptions = (selectedCounterpartyOption, allCounterparties, allFinancialArticles) => {
    if (!selectedCounterpartyOption?.value) return [];
    const cp = allCounterparties.find(c => c.id === selectedCounterpartyOption.value);
    if (!cp) return [];
    const allowedIds = safeParseIntArray(cp.allowed_articles);
    if (!allowedIds.length) return [];
    return allFinancialArticles.filter(fa => allowedIds.includes(fa.id)).map(fa => ({ value: fa.id, label: fa.name }));
};

const _getArticleOptionByName = (counterpartyOption, articleName, allCounterparties, allFinancialArticles) => {
    if (!counterpartyOption?.value || !articleName) return null;
    const options = _getArticleOptions(counterpartyOption, allCounterparties, allFinancialArticles);
    return options.find(opt => opt.label.toLowerCase() === articleName.toLowerCase()) || null;
};

const _getNomenclatureOptions = (selectedCounterpartyOption, selectedArticleOption, allCounterparties, allNomenclatures, allPriceTypesList) => {
    if (!selectedCounterpartyOption?.value || !selectedArticleOption?.value) return [];
    const cp = allCounterparties.find(c => c.id === selectedCounterpartyOption.value);
    if (!cp) return [];
    const priceLists = safeParseIntArray(cp.price_lists);
    const result = [];
    allNomenclatures.filter(n => n.article_id === selectedArticleOption.value).forEach(n => {
        if (n.prices && typeof n.prices === 'object') {
            Object.entries(n.prices).forEach(([ptidStr, val]) => {
                const ptid = parseInt(ptidStr, 10);
                if (priceLists.includes(ptid) && typeof val === 'number') {
                    const ptName = allPriceTypesList.find(pt => pt.id === ptid)?.name || `Тип ${ptid}`;
                    result.push({
                        value: `${n.id}-${ptid}`,
                        label: `${n.name} (${ptName}: ${val.toLocaleString('ru-RU')})`,
                        name: n.name, 
                        unit: n.unit, 
                        priceValue: val,
                        priceTypeName: ptName // Добавили это поле
                    });
                }
            });
        }
    });
    return result;
};

const _getNomenclatureOptionByDetails = (counterpartyOption, articleOption, rateName, unit, price, allCounterparties, allNomenclatures, allPriceTypesList, exactPriceMatch = true, customerName = null) => {
    if (!counterpartyOption?.value || !articleOption?.value || !rateName) return null;
    const options = _getNomenclatureOptions(counterpartyOption, articleOption, allCounterparties, allNomenclatures, allPriceTypesList);
    const same = options.filter(opt => opt.name.toLowerCase() === rateName.toLowerCase() && (opt.unit || '').toLowerCase() === (unit || '').toLowerCase());
    const exact = same.find(opt => opt.priceValue === price);
    if (exact) return exact;
    if (!exactPriceMatch) {
        const region = regionKeyFromCustomer(customerName);
        if (region) {
            const ranked = same
                .map((opt, index) => ({ opt, index, score: scorePriceList(opt.priceTypeName, region, customerName) }))
                .filter(item => item.score > 0)
                .sort((a, b) => b.score - a.score || a.index - b.index);
            if (ranked[0]) return ranked[0].opt;
        }
        return same[0] || null;
    }
    return null;
};

// --- КОМПОНЕНТЫ ЯЧЕЕК ---

const EditableCellSelect = React.memo(({ name, options, placeholder, onChange, disabled, rules, ...rest }) => (
    <Form.Item name={name} rules={rules} style={{ marginBottom: 0 }}>
        <Select 
            placeholder={placeholder} 
            options={options} 
            labelInValue 
            showSearch 
            disabled={disabled}
            tabIndex={disabled ? -1 : undefined}
            onChange={onChange} 
            style={{ width: '100%' }} 
            {...rest}
        />
    </Form.Item>
));

const EditablePriceInput = React.memo(({ name, formInstance, commonInputStyle, rules, onEnter }) => {
    // Наблюдаем за выбранной расценкой
    const selectedRateOption = Form.useWatch('selectedRateOption', formInstance);
    
    // Цена доступна для редактирования, если расценка не выбрана 
    // ИЛИ если в выбранной расценке цена равна 0
    const isPriceEditable = !selectedRateOption || (selectedRateOption && selectedRateOption.priceValue === 0);
    
    return (
        <Form.Item name={name} rules={rules} style={{ marginBottom: 0 }}>
            <InputNumber
                min={0} step={0.01}
                decimalSeparator=","
                // Если редактирование запрещено, делаем фон серым
                style={{ ...commonInputStyle, backgroundColor: isPriceEditable ? 'inherit' : '#f0f0f0' }}
                readOnly={!isPriceEditable}
                tabIndex={isPriceEditable ? undefined : -1}
                onPressEnter={onEnter}
            />
        </Form.Item>
    );
});

const CalculatedAmountDisplay = React.memo(({ formInstance, commonInputStyle }) => {
    const price = Form.useWatch('price', formInstance) || 0;
    const quantity = Form.useWatch('quantity', formInstance) || 0;
    const percent = Form.useWatch('percent', formInstance) || 0;
    const penaltyPercent = Form.useWatch('penalty', formInstance) || 0; // Теперь это процент

    // Базовая сумма
    const baseAmount = price * quantity * (percent / 100);
    // Сумма за вычетом процента штрафа
    const finalAmount = mathRound(baseAmount * (1 - penaltyPercent / 100));

    return (
        <InputNumber 
            value={finalAmount} 
            readOnly
            tabIndex={-1}
            decimalSeparator="," 
            style={{ ...commonInputStyle, backgroundColor: '#f0f0f0', fontWeight: 'bold' }} 
            formatter={value => value ? value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'} 
        />
    );
});

const FixedArticleSelectCell = React.memo(({ formInstance, rules, onChange, memoizedGetArticleOptions, currentCounterparty }) => {
    const articleOptions = useMemo(() => memoizedGetArticleOptions(currentCounterparty), [currentCounterparty, memoizedGetArticleOptions]);
    const isDisabled = !currentCounterparty || !articleOptions.length;
    const handleSelectChange = useCallback((option) => {
        formInstance.setFieldsValue({ article: option, selectedRateOption: null, unit: '', price: 0, rate_name: '' });
        if (onChange) onChange(option);
    }, [formInstance, onChange]);
    return <EditableCellSelect name="article" options={articleOptions} placeholder={isDisabled ? "Сначала выберите контрагента" : "Выберите статью"} rules={rules} disabled={isDisabled} onChange={handleSelectChange} />;
});

/*const FixedNomenclatureSelectCell = React.memo(({ formInstance, rules, onChange, memoizedGetNomenclatureOptions, currentCounterparty, currentArticle, onAfterChange }) => {
    const nomenclatureOptions = useMemo(() => memoizedGetNomenclatureOptions(currentCounterparty, currentArticle), [currentCounterparty, currentArticle, memoizedGetNomenclatureOptions]);

    const isDisabled = !currentArticle || !nomenclatureOptions.length;

    const handleSelectChange = useCallback((val) => {
        const selectedOption = nomenclatureOptions.find(opt => opt.value === val?.value);

        const currentQty = formInstance.getFieldValue('quantity') || 1;
        const currentPercent = formInstance.getFieldValue('percent') || 100;

        const newValues = {
            selectedRateOption: val,
            unit: selectedOption?.unit || '',
            price: selectedOption?.priceValue || 0,
            rate_name: selectedOption?.name || '',
            quantity: currentQty,
            percent: currentPercent
        };

        formInstance.setFieldsValue(newValues);

        if (onChange) onChange(val);
        if (onAfterChange) onAfterChange(newValues);

    }, [formInstance, onChange, nomenclatureOptions, onAfterChange]);

    return (
        <>
            <Form.Item name="rate_name" hidden style={{ display: 'none' }}>
                <Input />
            </Form.Item>

            <EditableCellSelect
                name="selectedRateOption"
                options={nomenclatureOptions}
                placeholder={isDisabled ? "Сначала выберите статью" : "Выберите расценку"}
                rules={rules}
                disabled={isDisabled}
                onChange={handleSelectChange}
            />
        </>
    );
});*/
const FixedNomenclatureSelectCell = React.memo(({ formInstance, rules, onChange, memoizedGetNomenclatureOptions, currentCounterparty, currentArticle, onAfterChange }) => {
    const rawOptions = useMemo(() => memoizedGetNomenclatureOptions(currentCounterparty, currentArticle), [currentCounterparty, currentArticle, memoizedGetNomenclatureOptions]);

    const nomenclatureOptions = useMemo(() => {
        return rawOptions.map(opt => ({
            ...opt,
            // 1. Это будет отображаться в строке выбора после того, как кликнули на пункт
            plainName: opt.name, 
            
            // 2. По этому полю будет работать поиск
            searchValue: `${opt.name} ${opt.priceTypeName} ${opt.priceValue}`.toLowerCase(),
            
            // 3. Это визуальный контент в самом выпадающем списке
            label: (
                <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                    <div style={{ fontWeight: 'bold', whiteSpace: 'normal', lineHeight: '1.2', color: '#262626' }}>
                        {opt.name}
                    </div>
                    <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '2px' }}>
                        Прайс: {opt.priceTypeName} | Цена: <span style={{ color: '#1890ff' }}>{opt.priceValue.toLocaleString('ru-RU')} р.</span>
                    </div>
                </div>
            )
        }));
    }, [rawOptions]);

    const isDisabled = !currentArticle || !nomenclatureOptions.length;

    const handleSelectChange = useCallback((val) => {
        const selectedOption = nomenclatureOptions.find(opt => opt.value === val?.value);
        const currentQty = formInstance.getFieldValue('quantity') || 1;
        const currentPercent = formInstance.getFieldValue('percent') || 100;

        const newValues = {
            selectedRateOption: {
                ...val,
                priceValue: selectedOption?.priceValue || 0,
                name: selectedOption?.name || ''
            },
            unit: selectedOption?.unit || '',
            price: selectedOption?.priceValue || 0,
            rate_name: selectedOption?.name || '',
            quantity: currentQty,
            percent: currentPercent
        };

        formInstance.setFieldsValue(newValues);
        if (onChange) onChange(val);
        if (onAfterChange) onAfterChange(newValues);
    }, [formInstance, onChange, nomenclatureOptions, onAfterChange]);

    return (
        <>
            <Form.Item name="rate_name" hidden style={{ display: 'none' }}>
                <Input tabIndex={-1} />
            </Form.Item>

            <EditableCellSelect
                name="selectedRateOption"
                options={nomenclatureOptions}
                placeholder={isDisabled ? "Сначала выберите статью" : "Выберите расценку"}
                rules={rules}
                disabled={isDisabled}
                onChange={handleSelectChange}
                
                // --- НАСТРОЙКИ ВИДА ---
                optionLabelProp="plainName" // Теперь после выбора будет только текст из поля plainName
                popupMatchSelectWidth={false} 
                dropdownStyle={{ width: 500 }} // Фиксированная ширина списка 500px
                
                // --- НАСТРОЙКА ПОИСКА ---
                filterOption={(input, option) => 
                    (option?.searchValue ?? '').includes(input.toLowerCase())
                }
            />
        </>
    );
});

/** Tab: внутри строки редактирования пропускаем readOnly/disabled. */
const focusNextEditableInRow = (fromEl, shiftKey) => {
    const row = fromEl?.closest?.('tr');
    if (!row) return false;
    const candidates = Array.from(
        row.querySelectorAll(
            'input:not([type="hidden"]), textarea, [tabindex]:not([tabindex="-1"]), .ant-select-selector, .ant-switch'
        )
    ).filter(el => {
        if (!el || el.disabled || el.readOnly) return false;
        if (el.getAttribute('aria-disabled') === 'true') return false;
        if (el.tabIndex < 0) return false;
        if (el.closest('.ant-select-disabled')) return false;
        if (el.closest('.ant-input-number-readonly, .ant-input-number-disabled')) {
            return false;
        }
        if (el.closest('[aria-hidden="true"]')) return false;
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') {
            return false;
        }
        return true;
    });
    if (!candidates.length) return false;
    const active =
        fromEl.closest('.ant-select-selector, .ant-input-number, .ant-switch') ||
        fromEl;
    let idx = candidates.findIndex(
        el => el === active || el.contains(fromEl) || fromEl.contains?.(el)
    );
    if (idx < 0) {
        idx = candidates.indexOf(document.activeElement);
    }
    const next = shiftKey
        ? candidates[(idx - 1 + candidates.length) % candidates.length]
        : candidates[(idx + 1) % candidates.length];
    if (!next || next === active) return false;
    const focusTarget =
        next.classList?.contains('ant-select-selector')
            ? next
            : next.querySelector?.('input') || next;
    focusTarget.focus?.();
    if (focusTarget.select && focusTarget.tagName === 'INPUT') {
        try {
            focusTarget.select();
        } catch (_) {
            /* ignore */
        }
    }
    return true;
};

// --- ОСНОВНОЙ КОМПОНЕНТ ---

const TasksPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [tasks, setTasks] = useState([]);
    const [selectedTasks, setSelectedTasks] = useState([]);
    const [bulkEstimating, setBulkEstimating] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentTaskId, setCurrentTaskId] = useState(null);
    const [dbTaskId, setDbTaskId] = useState(null);
    const [currentTaskStatus, setCurrentTaskStatus] = useState('new');
    const [
        taskClosedAt,
        setTaskClosedAt
    ] = useState(null);

    const [
        taskClosedAtSaving,
        setTaskClosedAtSaving
    ] = useState(false);
    const [invoices, setInvoices] = useState({ income: [], expense: [] });
    const [invoiceLinkHistoryKey, setInvoiceLinkHistoryKey] = useState(0);
    const [taskComments, setTaskComments] = useState('');
    const [taskHistory, setTaskHistory] = useState([]);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [externalTaskData, setExternalTaskData] = useState(null);
    const [expandedSections, setExpandedSections] = useState(['basic', 'income', 'expense']);

    const [
        gepardCheck,
        setGepardCheck
    ] = useState(null);

    const [
        gepardModalOpen,
        setGepardModalOpen
    ] = useState(false);

    const [
        gepardMismatchCodes,
        setGepardMismatchCodes
    ] = useState(() => new Set());

    const [
        gepardMismatchHints,
        setGepardMismatchHints
    ] = useState(() => ({}));

    const [
        gepardMissingInVdrCodes,
        setGepardMissingInVdrCodes
    ] = useState(() => new Set());

    const gepardHandledRef =
        useRef('');

    const [counterparties, setCounterparties] = useState([]);
    const [nomenclatures, setNomenclatures] = useState([]);
    const [financialArticles, setFinancialArticles] = useState([]);
    const [priceTypesList, setPriceTypesList] = useState([]);

    const [editingInvoice, setEditingInvoice] = useState(null);
    const [childTasks, setChildTasks] = useState([]);
    const [editInvoiceForm] = Form.useForm();

    const [scenarioCheckLoading, setScenarioCheckLoading] = useState(false);
    const [matchingScenarios, setMatchingScenarios] = useState([]);
    const [scenarioModalIsOpen, setScenarioModalIsOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [allScenarios, setAllScenarios] = useState([]); // Все сценарии из БД
    const [manualScenarioId, setManualScenarioId] = useState(null); // Выбранный в списке сценарий
    const [apiFilters, setApiFilters] = useState({
        onlyStatus: [], stage: [], type_id: [], assignedOrganizationId: [], organizationId: [],
        assigned_user_fio: '', status_invoices: '',
        region: '', object_id: '', filling_user: '',
        nomer_tt: '',
        // ПАРАМЕТРЫ ДЛЯ ТРЕХ ДАТ
        geCreated: null, leCreated: null,
        geExit: null, leExit: null,
        geUpdated: null, leUpdated: null,
        geClosed: null, leClosed: null,
        minProfitability: null,
        maxProfitability: null,
        news_type_name: [],
        filling_user: '',
        has_customer_order: null,
        has_supplier_order: null,
        has_daily_report: null,
        has_mail_discrepancies: null
    });
    const [availableStages, setAvailableStages] = useState([]);
    const [commentHistory, setCommentHistory] = useState([]);
    const commentsContainerRef = useRef(null);
    const historyContainerRef = useRef(null);
    const [availableTypes, setAvailableTypes] = useState([]); // Для списка видов работ
    const [isExporting, setIsExporting] = useState(false);
    const [
        exportTemplateOpen,
        setExportTemplateOpen
    ] = useState(false);
    const [
        exportTemplate,
        setExportTemplate
    ] = useState('full');
    // Улучшенная функция скролла: серия попыток для обхода анимаций
    const scrollToBottom = useCallback((ref) => {
        if (!ref.current) return;
        
        const scroll = () => {
            if (ref.current) {
                ref.current.scrollTop = ref.current.scrollHeight;
            }
        };

        // Делаем несколько попыток, чтобы поймать момент окончания анимации разворачивания
        scroll(); // мгновенно
        setTimeout(scroll, 100);
        setTimeout(scroll, 300);
        setTimeout(scroll, 600); // на случай долгой анимации модалки или аккордеона
    }, []);

    // Эффект: срабатывает при открытии модалки, загрузке данных ИЛИ раскрытии секций
    useEffect(() => {
        if (modalIsOpen && !invoiceLoading) {
            // Если секция "comments" раскрыта - скроллим
            if (expandedSections.includes('comments')) {
                scrollToBottom(commentsContainerRef);
            }
            // Если секция "history" раскрыта - скроллим
            if (expandedSections.includes('history')) {
                scrollToBottom(historyContainerRef);
            }
        }
    }, [
        modalIsOpen, 
        invoiceLoading, 
        expandedSections, // Важно: следим за раскрытием вкладок
        commentHistory.length, 
        taskHistory.length, 
        scrollToBottom
    ]);
    const fetchAvailableStages = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/tasks/statuses', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            // Преобразуем массив строк в формат для Select { value, label }
            const options = response.data.map(status => ({
                value: status,
                label: status
            }));
            setAvailableStages(options);
        } catch (e) {
            console.error("Ошибка при загрузке уникальных статусов:", e);
        }
    }, []);

    const fetchAvailableTypes = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/tasks/types', { 
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setAvailableTypes(response.data.map(t => ({ value: t, label: t })));
        } catch (e) {
            console.error("Ошибка при загрузке видов работ:", e);
        }
    }, []);

    const [availableUsers, setAvailableUsers] = useState([]);
    
// Функция для загрузки ТОЛЬКО комментариев и истории (для обновления без перезагрузки всей модалки)
    const fetchTaskMetadata = async (id) => {
        try {
            const token = localStorage.getItem('token');
            const [hist, comms] = await Promise.all([
                axios.get(`/api/tasks/${id}/history`, { headers: { 'Authorization': `Bearer ${token}` } }),
                axios.get(`/api/tasks/${id}/comments`, { headers: { 'Authorization': `Bearer ${token}` } })
            ]);
            setTaskHistory(hist.data || []);
            setCommentHistory(comms.data || []);
        } catch (e) {
            console.error("Ошибка обновления метаданных:", e);
        }
    };

// 2. Функция загрузки
    const fetchAvailableUsers = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/tasks/assigned-users', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setAvailableUsers(response.data.map(user => ({ value: user, label: user })));
        } catch (e) {
            console.error("Ошибка при загрузке списка исполнителей:", e);
        }
    }, []);
    const stageOptions = availableStages;
    const [availableObjects, setAvailableObjects] = useState([]);
    const [availableFillingUsers, setAvailableFillingUsers] = useState([]);

    const fetchAvailableFillingUsers = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/tasks/filling-users', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setAvailableFillingUsers(response.data.map(u => ({ value: u.login, label: u.fio || u.login })));
        } catch (e) {
            console.error("Ошибка загрузки списка заполняющих:", e);
        }
    }, []);


// 2. Функция загрузки
    const fetchAvailableObjects = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/tasks/service-objects', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setAvailableObjects(response.data.map(obj => ({ value: obj, label: obj })));
        } catch (e) {
            console.error("Ошибка при загрузке списка объектов:", e);
        }
    }, []);
    const [availableRegions, setAvailableRegions] = useState([]);

// 2. Функция загрузки
    const fetchAvailableRegions = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/tasks/regions', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setAvailableRegions(response.data.map(r => ({ value: r, label: r })));
        } catch (e) {
            console.error("Ошибка при загрузке списка регионов:", e);
        }
    }, []);
    const updateTaskStatus = async (newStatus) => {
        try {
            await axios.put(`/api/tasks/${dbTaskId}/status`, { status: newStatus }, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            setCurrentTaskStatus(newStatus);
            await fetchTaskMetadata(dbTaskId);
            setTasks(prev => prev.map(t => t.id === dbTaskId ? { ...t, status_invoices: newStatus } : t));
            message.success('Статус ВДР обновлен');
        } catch (e) {
            message.error('Ошибка обновления статуса');
            console.error(e);
        }
    };

    const memoizedGetArticleOptions = useCallback((cp) => _getArticleOptions(cp, counterparties, financialArticles), [counterparties, financialArticles]);
    const memoizedGetNomenclatureOptions = useCallback((cp, art) => _getNomenclatureOptions(cp, art, counterparties, nomenclatures, priceTypesList), [counterparties, nomenclatures, priceTypesList]);
    const counterpartyOptions = useMemo(() => counterparties.map(c => ({ value: c.id, label: c.name })), [counterparties]);

    const getDynamicValue = (placeholder, taskData, customFields) => {
        if (typeof placeholder !== 'string' || !placeholder.startsWith('<') || !placeholder.endsWith('>')) return placeholder;
        const fieldName = placeholder.slice(1, -1);
        if (Object.prototype.hasOwnProperty.call(taskData, fieldName)) return taskData[fieldName];
        const customFieldKey = Object.keys(customFields).find(k => k.toLowerCase() === fieldName.toLowerCase());
        return (customFieldKey && customFields[customFieldKey]) ? customFields[customFieldKey].value : null;
    };

    const handleApiFilterChange = (filterName, value) => {
        setApiFilters(prev => ({ ...prev, [filterName]: value }));
    };

   const applySelectedScenario = async (scenario) => {
    setScenarioModalIsOpen(false);
    setScenarioCheckLoading(true);
    const hideLoading = message.loading('Применение сценария...', 0);

    try {
        const token = localStorage.getItem('token');
        const allTemplatesResponse = await axios.get('/api/templates', {
            params: { limit: 10000 },
            headers: { 'Authorization': `Bearer ${token}` }
        });

        const allTemplates = allTemplatesResponse.data?.data || (Array.isArray(allTemplatesResponse.data) ? allTemplatesResponse.data : []);
        const templates = allTemplates.filter(template =>
            template.scenario_ids &&
            Array.isArray(template.scenario_ids) &&
            template.scenario_ids.some(id => String(id) === String(scenario.id))
        );

        if (!templates.length) {
            message.warning('Шаблоны для этого сценария не найдены.');
            return;
        }

        const customFields = externalTaskData.custom_fields ? JSON.parse(externalTaskData.custom_fields) : {};
        const newInvoices = { income: [], expense: [] };
        let warnings = [];

        for (const template of templates) {
            let counterparty = null;
            let driverPartner = null; // Для хранения объекта водителя, если КА — это машина
            const cpPlaceholder = template.counterparty;

            // --- 1. ПОИСК КОНТРАГЕНТА ---
            if (cpPlaceholder && cpPlaceholder.includes('+')) {
                // Логика связанных контрагентов (например, <Исполнитель> + car_id)
                const [primaryNamePH, secondaryField] = cpPlaceholder.split('+').map(s => s.trim());
                const primaryName = getDynamicValue(primaryNamePH, externalTaskData, customFields);

                if (primaryName) {
                    driverPartner = counterparties.find(c => c.name === primaryName);
                    if (driverPartner && driverPartner[secondaryField]) {
                        counterparty = counterparties.find(c => c.id === driverPartner[secondaryField]);
                        if (!counterparty) warnings.push(`Шаблон #${template.id}: Машина (ID ${driverPartner[secondaryField]}) не найдена.`);
                    } else {
                        warnings.push(`Шаблон #${template.id}: У ${primaryName} не заполнено поле "${secondaryField}".`);
                    }
                }
            } else {
                // Обычный поиск контрагента
                const cpName = getDynamicValue(cpPlaceholder, externalTaskData, customFields);
                if (cpName) {
                    counterparty = counterparties.find(c => c.name && c.name.toLowerCase() === cpName.toLowerCase());
                }
            }

            if (!counterparty) continue;

            // --- 2. ПРОВЕРКА СТАТЬИ И АВТО-ЛОГИКИ ---
            const article = financialArticles.find(a => a.name && template.article_id && a.name.toLowerCase() === template.article_id.toLowerCase());
            if (!article) {
                warnings.push(`Шаблон #${template.id}: Статья "${template.article_id}" не найдена.`);
                continue;
            }

            const isPersonalArt = article.name === '5. Личный автомобиль';
            const isAmortizationArt = article.name === '6. Амортизация служебного автомобиля';

            // ЛОГИКА ФИЛЬТРАЦИИ ПО ГАЛОЧКЕ
            // А) Если это Амортизация (ст. 6) и КА является привязанной машиной водителя
            if (isAmortizationArt && driverPartner) {
                if (!!driverPartner.has_personal_car === true) {
                    // Если водитель на личном авто, амортизация служебного не применяется
                    continue; 
                }
            }

            // Б) Если это Личный автомобиль (ст. 5) (обычно шаблон на самого водителя)
            if (isPersonalArt) {
                if (!!counterparty.has_personal_car === false) {
                    // Если у водителя не стоит галочка, статью "Личный авто" не применяем
                    continue;
                }
            }

            // Стандартная проверка: разрешена ли статья в карточке текущего КА (водителя или машины)
            const allowedArticleIds = safeParseIntArray(counterparty.allowed_articles);
            if (!allowedArticleIds.includes(article.id)) {
                warnings.push(`Шаблон #${template.id}: Статья "${article.name}" не разрешена для ${counterparty.name}.`);
                continue; 
            }

            // --- 3. ПРОВЕРКА НОМЕНКЛАТУРЫ И ЦЕНЫ ---
            const nomenclature = nomenclatures.find(n => n.name && template.nomenclature_id && n.name.toLowerCase() === template.nomenclature_id.toLowerCase());
            if (!nomenclature) {
                warnings.push(`Шаблон #${template.id}: Номенклатура "${template.nomenclature_id}" не найдена.`);
                continue;
            }

            const customerName = getDynamicValue('<Zakazchik>', externalTaskData, customFields);
            const selectedRate = pickNomenclatureRate(nomenclature, counterparty, customerName, priceTypesList);

            if (!selectedRate) {
                warnings.push(`Шаблон #${template.id}: У ${counterparty.name} нет цены для "${nomenclature.name}" в разрешенных прайсах.`);
                continue;
            }

            // --- 4. РАСЧЕТ И ДОБАВЛЕНИЕ СТРОКИ ---
            const calculatedVars = calculateTemplateVariables(externalTaskData, customFields, counterparty, getDynamicValue);
            
            let price = selectedRate.priceValue;
            let quantity = toNumber(resolveTemplateValue(template.quantity, externalTaskData, customFields, counterparty, calculatedVars, getDynamicValue)) || 0;

            // Округление для ГСМ и километража
            if (article.name.startsWith('3.2') || article.name.startsWith('3.1.1')) {
                quantity = mathRound(quantity);
            }

            const penalty = toNumber(resolveTemplateValue(template.penalty, externalTaskData, customFields, counterparty, calculatedVars, getDynamicValue)) || 0;
            const percent = toNumber(resolveTemplateValue(template.percentage, externalTaskData, customFields, counterparty, calculatedVars, getDynamicValue)) || 100;
            const note = resolveTemplateValue(template.note, externalTaskData, customFields, counterparty, calculatedVars, getDynamicValue) || '';

            newInvoices[template.type].push({
                id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                type: template.type,
                counterparty: counterparty.name,
                article: article.name,
                rate_name: nomenclature.name,
                unit: nomenclature.unit,
                price: price,
                quantity: quantity,
                percent: percent,
                note: note,
                penalty: penalty,
                include: template.is_active !== false,
                amount: mathRound(price * quantity * (percent / 100))
            });
        }

        // --- 5. ВЫВОД РЕЗУЛЬТАТОВ ---
        if (newInvoices.income.length > 0 || newInvoices.expense.length > 0) {
            setInvoices(prev => ({
                income: [...prev.income, ...newInvoices.income],
                expense: [...prev.expense, ...newInvoices.expense]
            }));
            setHasUnsavedChanges(true);
            message.success(`ВДР успешно обновлен на основе шаблонов`);
        } else {
            message.warning("Ничего не добавлено. Проверьте условия в шаблонах и настройки КА.");
        }

        if (warnings.length) {
            notification.warning({
                message: 'Замечания по применению шаблонов',
                description: (
                    <div style={{ maxHeight: 200, overflow: 'auto' }}>
                        {warnings.map((w, i) => <div key={i} style={{marginBottom: 4}}>• {w}</div>)}
                    </div>
                ),
                duration: 10
            });
        }

    } catch (e) {
        console.error("Ошибка применения сценария:", e);
        message.error('Ошибка при обработке шаблонов');
    } finally {
        hideLoading();
        setScenarioCheckLoading(false);
    }
};

    const handleClearByType = type => {
        const currentRows =
            Array.isArray(invoices[type])
                ? invoices[type]
                : [];

        const lockedRows =
            currentRows.filter(
                isInvoiceLocked
            );

        const freeRows =
            currentRows.filter(
                row =>
                    !isInvoiceLocked(row)
            );

        if (freeRows.length === 0) {
            message.warning(
                lockedRows.length > 0
                    ? 'Все строки раздела включены в документы и заблокированы'
                    : 'Раздел уже пуст'
            );

            return;
        }

        /*
        * Оставляем заблокированные строки,
        * удаляем только свободные.
        */
        setInvoices(prev => ({
            ...prev,
            [type]:
                prev[type].filter(
                    isInvoiceLocked
                )
        }));

        setHasUnsavedChanges(true);

        if (lockedRows.length > 0) {
            message.info(
                `Удалено свободных строк: ${freeRows.length}. ` +
                `Заблокированных оставлено: ${lockedRows.length}.`
            );
        } else {
            message.info(
                type === 'income'
                    ? 'Доходы очищены'
                    : 'Расходы очищены'
            );
        }
    };

    const handleFindAndApplyScenario = async () => {
        if (!externalTaskData) return message.warning('Данные задачи еще не загружены.');
        setScenarioCheckLoading(true);
        try {
            const token = localStorage.getItem('token');
            const scenariosResponse = await axios.get('/api/scenarios', { params: { limit: 1000 }, headers: { 'Authorization': `Bearer ${token}` } });
            const allScenarios = scenariosResponse.data?.data || [];
            const customFields = externalTaskData.custom_fields ? JSON.parse(externalTaskData.custom_fields) : {};
            const matched = [];

            console.log('=== ЗАПУСК ПОИСКА СЦЕНАРИЕВ ===');
console.log('Входные данные задачи (customFields):', customFields);

for (const scenario of allScenarios) {
    console.groupCollapsed(`Проверка сценария: "${scenario.scenario_name}"`);
    let isMatch = true;

    for (const field of scenario.fields) {
        if (field.system_name === 'news_date') {
            // ... (логика проверки даты без изменений)
            if (!externalTaskData.news_date) { isMatch = false; break; }
            if (field.possible_values?.length) {
                const checks = await Promise.all(field.possible_values.map(c => checkDateTimeCondition(externalTaskData.news_date, c)));
                if (!checks.some(r => r)) { isMatch = false; break; }
            }
        } else {
            // Получаем значение из задачи
            const val = getDynamicValue(`<${field.system_name}>`, externalTaskData, customFields);
            
            // Пытаемся преобразовать в число (обработка пустоты и запятых)
            const taskValNum = (val !== undefined && val !== null && val !== '') 
                ? parseFloat(val.toString().replace(',', '.')) 
                : null;

            if (field.possible_values?.length) {
                const matchFound = field.possible_values.some(v => {
                    const scenarioValStr = String(v).trim().toLowerCase();

                    // --- ЛОГИКА "0" (Поля нет ИЛИ оно равно 0) ---
                    if (scenarioValStr === '0') {
                        const isZeroOrNull = val === undefined || val === null || val === '' || taskValNum === 0;
                        if (isZeroOrNull) console.log(`    ✅ [0] Поле <${field.system_name}> пустое или равно 0`);
                        return isZeroOrNull;
                    }

                    // --- ЛОГИКА ">0" (Поле ЕСТЬ и оно > 0) ---
                    if (scenarioValStr === '>0') {
                        const isMoreThanZero = taskValNum !== null && !isNaN(taskValNum) && taskValNum > 0;
                        if (isMoreThanZero) console.log(`    ✅ [>0] Поле <${field.system_name}> равно ${taskValNum} (> 0)`);
                        return isMoreThanZero;
                    }

                    // --- ОБЫЧНОЕ СРАВНЕНИЕ (Строки: true, false, названия и т.д.) ---
                    if (val !== undefined && val !== null) {
                        const isExact = scenarioValStr === val.toString().trim().toLowerCase();
                        if (isExact) console.log(`    ✅ Совпадение строки: "${val}"`);
                        return isExact;
                    }

                    return false;
                });

                if (!matchFound) {
                    console.warn(`  ❌ Поле <${field.system_name}> не подошло (ожидалось: ${field.possible_values})`);
                    isMatch = false;
                    break;
                }
            } else {
                // Если в сценарии список возможных значений пуст - просто проверяем физическое наличие поля
                if (val === undefined || val === null) {
                    console.warn(`  ❌ Поле <${field.system_name}> обязательно должно существовать`);
                    isMatch = false;
                    break;
                }
            }
        }
    }

    if (isMatch) {
        console.info(`🎯 ПОДХОДИТ: "${scenario.scenario_name}"`);
        matched.push(scenario);
    }
    console.groupEnd();
}

console.log(`=== ПОИСК ЗАВЕРШЕН. Найдено совпадений: ${matched.length} ===`)

            if (!matched.length) message.info('Подходящих сценариев нет.');
            else if (matched.length === 1) applySelectedScenario(matched[0]);
            else { setMatchingScenarios(matched); setScenarioModalIsOpen(true); }
        } catch (e) { message.error('Ошибка поиска сценариев'); } finally { setScenarioCheckLoading(false); }
    };

    const matchScenariosForTask = async (taskData, scenarios) => {
        const customFields = parseCustomFields(taskData);
        const matched = [];
        for (const scenario of scenarios) {
            let isMatch = true;
            for (const field of scenario.fields || []) {
                if (field.system_name === 'news_date') {
                    if (!taskData.news_date) { isMatch = false; break; }
                    if (field.possible_values?.length) {
                        const checks = await Promise.all(field.possible_values.map(c => checkDateTimeCondition(taskData.news_date, c)));
                        if (!checks.some(Boolean)) { isMatch = false; break; }
                    }
                } else {
                    const val = getDynamicValue(`<${field.system_name}>`, taskData, customFields);
                    const taskValNum = (val !== undefined && val !== null && val !== '')
                        ? parseFloat(val.toString().replace(',', '.'))
                        : null;
                    if (field.possible_values?.length) {
                        const matchFound = field.possible_values.some(v => {
                            const scenarioValStr = String(v).trim().toLowerCase();
                            if (scenarioValStr === '0') {
                                return val === undefined || val === null || val === '' || taskValNum === 0;
                            }
                            if (scenarioValStr === '>0') {
                                return taskValNum !== null && !isNaN(taskValNum) && taskValNum > 0;
                            }
                            if (val !== undefined && val !== null) {
                                return scenarioValStr === val.toString().trim().toLowerCase();
                            }
                            return false;
                        });
                        if (!matchFound) { isMatch = false; break; }
                    } else if (val === undefined || val === null) {
                        isMatch = false;
                        break;
                    }
                }
            }
            if (isMatch) matched.push(scenario);
        }
        return matched;
    };

    const buildInvoicesFromScenario = (scenario, taskData, dicts) => {
        const customFields = parseCustomFields(taskData);
        const templates = (dicts.templates || []).filter(template =>
            template.scenario_ids &&
            Array.isArray(template.scenario_ids) &&
            template.scenario_ids.some(id => String(id) === String(scenario.id))
        );
        const newInvoices = { income: [], expense: [] };
        const warnings = [];
        if (!templates.length) return { invoices: newInvoices, warnings, emptyTemplates: true };

        for (const template of templates) {
            let counterparty = null;
            let driverPartner = null;
            const cpPlaceholder = template.counterparty;
            if (cpPlaceholder && cpPlaceholder.includes('+')) {
                const [primaryNamePH, secondaryField] = cpPlaceholder.split('+').map(s => s.trim());
                const primaryName = getDynamicValue(primaryNamePH, taskData, customFields);
                if (primaryName) {
                    driverPartner = dicts.counterparties.find(c => c.name === primaryName);
                    if (driverPartner && driverPartner[secondaryField]) {
                        counterparty = dicts.counterparties.find(c => c.id === driverPartner[secondaryField]);
                        if (!counterparty) warnings.push(`Шаблон #${template.id}: Машина (ID ${driverPartner[secondaryField]}) не найдена.`);
                    } else {
                        warnings.push(`Шаблон #${template.id}: У ${primaryName} не заполнено поле "${secondaryField}".`);
                    }
                }
            } else {
                const cpName = getDynamicValue(cpPlaceholder, taskData, customFields);
                if (cpName) {
                    counterparty = dicts.counterparties.find(c => c.name && c.name.toLowerCase() === String(cpName).toLowerCase());
                }
            }
            if (!counterparty) continue;
            const article = dicts.financialArticles.find(a => a.name && template.article_id && a.name.toLowerCase() === template.article_id.toLowerCase());
            if (!article) {
                warnings.push(`Шаблон #${template.id}: Статья "${template.article_id}" не найдена.`);
                continue;
            }
            const isPersonalArt = article.name === '5. Личный автомобиль';
            const isAmortizationArt = article.name === '6. Амортизация служебного автомобиля';
            if (isAmortizationArt && driverPartner && !!driverPartner.has_personal_car === true) continue;
            if (isPersonalArt && !!counterparty.has_personal_car === false) continue;
            const allowedArticleIds = safeParseIntArray(counterparty.allowed_articles);
            if (!allowedArticleIds.includes(article.id)) {
                warnings.push(`Шаблон #${template.id}: Статья "${article.name}" не разрешена для ${counterparty.name}.`);
                continue;
            }
            const nomenclature = dicts.nomenclatures.find(n => n.name && template.nomenclature_id && n.name.toLowerCase() === template.nomenclature_id.toLowerCase());
            if (!nomenclature) {
                warnings.push(`Шаблон #${template.id}: Номенклатура "${template.nomenclature_id}" не найдена.`);
                continue;
            }
            const customerName = getDynamicValue('<Zakazchik>', taskData, customFields);
            const selectedRate = pickNomenclatureRate(nomenclature, counterparty, customerName, dicts.priceTypesList);
            if (!selectedRate) {
                warnings.push(`Шаблон #${template.id}: У ${counterparty.name} нет цены для "${nomenclature.name}" в разрешенных прайсах.`);
                continue;
            }
            const calculatedVars = calculateTemplateVariables(taskData, customFields, counterparty, getDynamicValue);
            let price = selectedRate.priceValue;
            let quantity = toNumber(resolveTemplateValue(template.quantity, taskData, customFields, counterparty, calculatedVars, getDynamicValue)) || 0;
            if (article.name.startsWith('3.2') || article.name.startsWith('3.1.1')) quantity = mathRound(quantity);
            const penalty = toNumber(resolveTemplateValue(template.penalty, taskData, customFields, counterparty, calculatedVars, getDynamicValue)) || 0;
            const percent = toNumber(resolveTemplateValue(template.percentage, taskData, customFields, counterparty, calculatedVars, getDynamicValue)) || 100;
            const note = resolveTemplateValue(template.note, taskData, customFields, counterparty, calculatedVars, getDynamicValue) || '';
            if (template.type !== 'income' && template.type !== 'expense') continue;
            newInvoices[template.type].push({
                id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                type: template.type,
                counterparty: counterparty.name,
                article: article.name,
                rate_name: nomenclature.name,
                unit: nomenclature.unit,
                price,
                quantity,
                percent,
                note,
                penalty,
                include: template.is_active !== false,
                amount: mathRound(price * quantity * (percent / 100))
            });
        }
        return { invoices: newInvoices, warnings, emptyTemplates: false };
    };

    const runBulkAutoEstimate = async () => {
        const batch = selectedTasks.slice();
        if (!batch.length) {
            message.warning('Выберите заявки в таблице');
            return;
        }
        setBulkEstimating(true);
        const hide = message.loading('Автоосмечивание...', 0);
        const saved = [];
        const skipped = [];
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const [cp, nom, sc, tpl] = await Promise.all([
                axios.get('/api/counterparties?limit=1000', { headers }),
                axios.get('/api/nomenclature?limit=10000', { headers }),
                axios.get('/api/scenarios?limit=1000', { headers }),
                axios.get('/api/templates', { params: { limit: 10000 }, headers })
            ]);
            const dicts = {
                counterparties: cp.data?.data || [],
                nomenclatures: nom.data?.data || [],
                financialArticles: cp.data?.options?.articles || [],
                priceTypesList: cp.data?.options?.priceTypes || nom.data?.priceTypes || [],
                templates: tpl.data?.data || (Array.isArray(tpl.data) ? tpl.data : [])
            };
            const scenarios = sc.data?.data || [];
            setCounterparties(dicts.counterparties);
            setNomenclatures(dicts.nomenclatures);
            setFinancialArticles(dicts.financialArticles);
            setPriceTypesList(dicts.priceTypesList);
            setAllScenarios(scenarios);

            for (let i = 0; i < batch.length; i += 1) {
                const record = batch[i];
                const label = record.external_id || record.id;
                hide();
                message.loading({ content: `Автоосмечивание ${i + 1} из ${batch.length}`, key: 'bulk-est', duration: 0 });
                try {
                    const taskData = taskViewFromRecord(record);
                    if (!taskData.custom_fields && !taskData.news_date) {
                        skipped.push(`${label}: нет данных заявки`);
                        continue;
                    }
                    const matched = await matchScenariosForTask(taskData, scenarios);
                    const withTemplates = matched.filter(scenario =>
                        dicts.templates.some(template =>
                            Array.isArray(template.scenario_ids) &&
                            template.scenario_ids.some(id => String(id) === String(scenario.id))
                        )
                    );
                    if (!withTemplates.length) {
                        skipped.push(`${label}: нет сценария с шаблонами`);
                        continue;
                    }
                    if (withTemplates.length > 1) {
                        skipped.push(`${label}: несколько сценариев (${withTemplates.map(s => s.scenario_name).join(', ')})`);
                        continue;
                    }
                    const existing = await axios.get(`/api/tasks/${record.id}/invoices`, { headers });
                    const incomeNow = Array.isArray(existing.data?.income) ? existing.data.income : [];
                    const expenseNow = Array.isArray(existing.data?.expense) ? existing.data.expense : [];
                    if (incomeNow.length || expenseNow.length) {
                        skipped.push(`${label}: строки ВДР уже есть`);
                        continue;
                    }
                    const built = buildInvoicesFromScenario(withTemplates[0], taskData, dicts);
                    const income = built.invoices.income;
                    const expense = built.invoices.expense;
                    if (!income.length && !expense.length) {
                        skipped.push(`${label}: шаблоны ничего не добавили`);
                        continue;
                    }
                    await axios.put(`/api/tasks/${record.id}/invoices`, { income, expense }, { headers });
                    saved.push(`${label}: ${withTemplates[0].scenario_name} (доход ${income.length}, расход ${expense.length})`);
                } catch (error) {
                    skipped.push(`${label}: ${error?.response?.data?.error || error.message || 'ошибка'}`);
                }
            }
            message.destroy('bulk-est');
            setSelectedTasks([]);
            fetchTasks();
            Modal.info({
                title: 'Автоосмечивание',
                width: 640,
                content: (
                    <div style={{ maxHeight: 360, overflow: 'auto' }}>
                        <div>Записано: {saved.length}. Статусы заявок не менялись.</div>
                        {saved.map(line => <div key={line}>{line}</div>)}
                        {skipped.length ? <div style={{ marginTop: 8 }}>Пропущено: {skipped.length}</div> : null}
                        {skipped.map(line => <div key={line}>{line}</div>)}
                    </div>
                )
            });
        } catch (error) {
            message.destroy('bulk-est');
            message.error('Не удалось выполнить автоосмечивание');
        } finally {
            hide();
            message.destroy('bulk-est');
            setBulkEstimating(false);
        }
    };

    const confirmBulkAutoEstimate = () => {
        if (!selectedTasks.length) {
            message.warning('Выберите заявки в таблице');
            return;
        }
        Modal.confirm({
            title: 'Автоосмечивание',
            content: `Добавить строки ВДР по сценарию в выбранные заявки (${selectedTasks.length})? Статус не изменится. Заявки, где строки уже есть, будут пропущены.`,
            okText: 'Осметить',
            cancelText: 'Отмена',
            onOk: () => runBulkAutoEstimate()
        });
    };

    const clearAllInvoices = () => {
        setInvoices({ income: [], expense: [] });
        setEditingInvoice(null);
        editInvoiceForm.resetFields();
        setHasUnsavedChanges(true);
        message.info('Список очищен. Не забудьте сохранить изменения.');
    };

    useEffect(() => {
        fetchTasks();
        fetchAvailableStages();
        fetchAvailableUsers();
        fetchAvailableObjects();
        fetchAvailableRegions();
        fetchAvailableTypes();
        fetchAvailableFillingUsers()
        }, [pagination.current, pagination.pageSize]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            // Работаем только если модалка открыта и мы не редактируем сейчас строку (чтобы не было конфликтов)
            if (!modalIsOpen || editingInvoice) return;

            // Проверяем, не находится ли фокус в поле ввода (чтобы при печати не создавались строки)
            const activeTag = document.activeElement.tagName;
            if (activeTag === 'INPUT' || activeTag === 'TEXTAREA' || document.activeElement.isContentEditable) {
                return;
            }

            // Клавиша "+" (на основной клавиатуре это обычно Shift + = или просто = )
            if (e.key === '+' || e.key === '=') {
                e.preventDefault();
                handleAddNewInlineInitiate('income');
            }
            // Клавиша "-"
            else if (e.key === '-') {
                e.preventDefault();
                handleAddNewInlineInitiate('expense');
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [modalIsOpen, editingInvoice]);

    const fetchTasks = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            let response;
            if (searchText.trim()) {
                try {
                    response = await axios.get(`/api/tasks/${encodeURIComponent(searchText.trim())}`, { headers: { 'Authorization': `Bearer ${token}` } });
                    const data = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []);
                    setTasks(data); setPagination(p => ({ ...p, total: data.length }));
                } catch (e) { setTasks([]); setPagination(p => ({ ...p, total: 0 })); }
            } else {
                const params = { page: pagination.current, limit: pagination.pageSize, ...apiFilters };
                const queryParams = {};
                for (const key in params) {
                    const val = params[key];
                    if (val === null || val === undefined || val === '') continue;
                    if (dayjs.isDayjs(val)) {
                        /*
                         * Для верхней границы даты закрытия:
                         * полночь = конец выбранного дня
                         * (иначе RangePicker отсекает почти весь день).
                         */
                        if (
                            key === 'leClosed' &&
                            val.hour() === 0 &&
                            val.minute() === 0 &&
                            val.second() === 0
                        ) {
                            queryParams[key] = val
                                .endOf('day')
                                .format('YYYY-MM-DD HH:mm:ss');
                        } else {
                            queryParams[key] = val.format(
                                'YYYY-MM-DD HH:mm:ss'
                            );
                        }
                    } else if (Array.isArray(val)) {
                        if (val.length > 0) queryParams[key] = val.join(',');
                    } else {
                        queryParams[key] = val;
                    }
                }
                response = await axios.get('/api/tasks', { params: queryParams, headers: { 'Authorization': `Bearer ${token}` } });
                setTasks(response.data || []); setPagination(p => ({ ...p, total: parseInt(response.headers['x-total-count'] || 0) }));
            }
        } catch (e) { message.error('Ошибка загрузки задач'); } finally { setLoading(false); }
    };

    const applyFilters = async () => { setPagination(prev => ({ ...prev, current: 1 })); await fetchTasks(); };
    const resetFilters = () => {
        setApiFilters({
            onlyStatus: [], stage: [], type_id: [], assignedOrganizationId: [], organizationId: [],
            assigned_user_fio: '', status_invoices: '', region: '', object_id: '', filling_user: '',
            nomer_tt: '', // <--- ДОБАВИТЬ ЭТО
            geCreated: null, leCreated: null, geExit: null, leExit: null, geUpdated: null, leUpdated: null,
            minProfitability: null, maxProfitability: null, news_type_name: [],has_customer_order: null,
            has_supplier_order: null,
            geClosed: null,
            leClosed: null,
            has_daily_report: null,
            has_mail_discrepancies: null
        });
        setSearchText('');
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchTasks();
    };

    const syncTasks = async () => {
        try {
            message.loading({ content: 'Синхронизация...', key: 'sync' });
            await axios.get('/api/tasks/sync', { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            fetchTasks(); message.success({ content: 'Готово', key: 'sync' });
        } catch (error) { message.error({ content: 'Ошибка синхронизации', key: 'sync' }); }
    };

    const formatCustomDate = (val) => {
        if (!val || val === 'false') return '-';
        try {
            // 1. Проверяем, является ли значение Unix-таймстампом
            const isNumeric = /^\d+$/.test(val.toString());

            if (isNumeric) {
                const num = val.toString().length === 10 ? Number(val) * 1000 : Number(val);
                let date = dayjs(num);

                // МАТЕМАТИЧЕСКАЯ ФИКСАЦИЯ МСК (+3 часа)
                const pcOffset = date.utcOffset();
                const mskOffset = 180;
                date = date.add(mskOffset - pcOffset, 'minute');

                return date.format('DD.MM.YYYY HH:mm');
            }

            // 2. Если это строка (например, "02.03.2026 01:09:00")
            const parsedDate = dayjs(val, "DD.MM.YYYY HH:mm:ss");
            if (parsedDate.isValid()) {
                return parsedDate.format('DD.MM.YYYY HH:mm');
            }

            // 3. КРИТИЧЕСКИЙ ФОЛЛБЕК
            return val.toString().substring(0, 16);
        } catch (e) {
            return '-';
        }
    };

    const openExportTemplateModal = () => {
        setExportTemplate('full');
        setExportTemplateOpen(true);
    };

    // --- ЭКСПОРТ ВДР: полный или сокращённый шаблон ---
    const exportAllInvoices = async (
        template = 'full'
    ) => {
        const token = localStorage.getItem('token');
        const isShort = template === 'short';

        setExportTemplateOpen(false);
        setIsExporting(true);
        const hideLoading = message.loading(
            isShort
                ? 'Формирование сокращённого экспорта ВДР...'
                : 'Формирование полного экспорта ВДР...',
            0
        );

        try {
            const params = {
                search: searchText,
                ...apiFilters
            };
            const queryParams = {};

            for (const key in params) {
                const val = params[key];
                if (
                    val === null ||
                    val === undefined ||
                    val === ''
                ) {
                    continue;
                }

                if (dayjs.isDayjs(val)) {
                    if (
                        key === 'leClosed' &&
                        val.hour() === 0 &&
                        val.minute() === 0 &&
                        val.second() === 0
                    ) {
                        queryParams[key] = val
                            .endOf('day')
                            .format('YYYY-MM-DD HH:mm:ss');
                    } else {
                        queryParams[key] = val.format(
                            'YYYY-MM-DD HH:mm:ss'
                        );
                    }
                } else if (Array.isArray(val)) {
                    if (val.length > 0) {
                        queryParams[key] = val.join(',');
                    }
                } else {
                    queryParams[key] = val;
                }
            }

            const response = await axios.get(
                '/api/invoices/export-all-json',
                {
                    params: queryParams,
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const data = response.data || {};
            const list = Array.isArray(data.invoices)
                ? data.invoices
                : [];
            const tasksMap =
                data.tasks &&
                typeof data.tasks === 'object'
                    ? data.tasks
                    : {};

            if (!list.length) {
                message.warning('Нет данных для экспорта');
                return;
            }

            const customFieldsCache = new Map();

            const getTaskCustomFields = task => {
                const taskId = String(task?.id || '');

                if (customFieldsCache.has(taskId)) {
                    return customFieldsCache.get(taskId);
                }

                let customFields = {};

                try {
                    const rawData =
                        typeof task?.raw_data === 'string'
                            ? JSON.parse(task.raw_data)
                            : task?.raw_data || {};

                    customFields =
                        typeof rawData?.custom_fields ===
                        'string'
                            ? JSON.parse(
                                  rawData.custom_fields
                              )
                            : rawData?.custom_fields || {};
                } catch (error) {
                    console.warn(
                        'Ошибка разбора custom_fields задачи:',
                        task?.id,
                        error
                    );
                    customFields = {};
                }

                customFieldsCache.set(
                    taskId,
                    customFields
                );
                return customFields;
            };

            const invoiceIncluded = inv => {
                const value =
                    inv?.included ?? inv?.include;
                if (
                    value &&
                    typeof value === 'object'
                ) {
                    if (Array.isArray(value.data)) {
                        return Number(value.data[0]) === 1;
                    }
                    if (value.data != null) {
                        return Number(value.data) === 1;
                    }
                }
                return (
                    value === true ||
                    value === 1 ||
                    value === '1'
                );
            };

            const shortHeaders = [
                'ID Задачи',
                'Внешний ID',
                'Вид работ',
                'Исполнитель',
                'Номер',
                'Объект',
                'Описание выполненных работ',
                'Контрагент',
                'Статья',
                'Наименование расценки',
                'Ед. изм.',
                'Кол-во',
                'Цена',
                'Стоимость',
                'Дата появления',
                'Дата покидания',
                '%',
                'Примечание',
                'Учитывать'
            ];

            const getWorkDescription = cf => {
                // Текст в Opisanie_vepolnenneh_rabot (с лишней n);
                // Opisanie_vepolneneh_rabot часто boolean true/false — его пропускаем.
                const keys = [
                    'Opisanie_vepolnenneh_rabot',
                    'Opisanie_vypolnennyh_rabot',
                    'Opisanie_vepolneneh_rabot'
                ];

                for (const key of keys) {
                    const raw =
                        cf?.[key]?.value ?? cf?.[key];

                    if (typeof raw === 'boolean') continue;
                    if (
                        raw === undefined ||
                        raw === null ||
                        typeof raw === 'object'
                    ) {
                        continue;
                    }

                    const text = String(raw).trim();
                    if (
                        !text ||
                        text === '-' ||
                        text === 'true' ||
                        text === 'false'
                    ) {
                        continue;
                    }

                    return text;
                }

                return '';
            };

            const wsData = list.map(inv => {
                const task =
                    tasksMap[String(inv.task_id)] ||
                    tasksMap[Number(inv.task_id)] ||
                    {};

                const cf = getTaskCustomFields(task);
                const getVal = key => cf[key]?.value ?? '-';
                const workDescription =
                    getWorkDescription(cf) ||
                    task.work_description ||
                    '';

                const appearanceDate = getVal(
                    'Vremja_pojavlenija_na_sayte'
                );
                const exitDate = getVal(
                    'Vremja_pokidanija_sayta'
                );

                if (isShort) {
                    // Сокращённый шаблон — поля как на образце
                    return {
                        'ID Задачи': inv.task_id || '-',
                        'Внешний ID':
                            task.external_id || '-',
                        'Вид работ':
                            task.news_type_name || '-',
                        Исполнитель:
                            task.assigned_user_fio || '-',
                        Номер: getVal('Nomer_TT'),
                        Объект: getVal('Nomer_BS'),
                        'Описание выполненных работ':
                            workDescription || '',
                        Контрагент:
                            inv.counterparty || '-',
                        Статья: inv.article || '-',
                        'Наименование расценки':
                            inv.rate_name || '-',
                        'Ед. изм.': inv.unit || '-',
                        'Кол-во':
                            parseFloat(inv.quantity) || 0,
                        Цена: parseFloat(inv.price) || 0,
                        Стоимость:
                            parseFloat(inv.amount) || 0,
                        'Дата появления':
                            formatCustomDate(
                                appearanceDate
                            ),
                        'Дата покидания':
                            formatCustomDate(exitDate),
                        '%': inv.percent || '-',
                        Примечание: inv.note || '',
                        Учитывать: invoiceIncluded(inv)
                            ? 'Да'
                            : 'Нет'
                    };
                }

                const row = {
                    'ID Задачи': inv.task_id || '-',
                    'Внешний ID': task.external_id || '-',
                    'Вид работ':
                        task.news_type_name || '-',
                    Исполнитель:
                        task.assigned_user_fio || '-',
                    'Время обновления (ВДР)':
                        inv.updated_at
                            ? dayjs(inv.updated_at).format(
                                  'DD.MM.YYYY HH:mm'
                              )
                            : '-',
                    'Дата создания ВДР': inv.created_at
                        ? dayjs(inv.created_at).format(
                              'DD.MM.YYYY HH:mm'
                          )
                        : '-',
                    Заголовок: task.title || '-',
                    'Номер ТТ': getVal('Nomer_TT'),
                    'Объект (БС)': getVal('Nomer_BS'),
                    Тип:
                        inv.type === 'income'
                            ? 'Доход'
                            : 'Расход',
                    Контрагент: inv.counterparty || '-',
                    Статья: inv.article || '-',
                    'Наименование расценки':
                        inv.rate_name || '-',
                    'Ед. изм.': inv.unit || '-',
                    'Кол-во':
                        parseFloat(inv.quantity) || 0,
                    Цена: parseFloat(inv.price) || 0,
                    Стоимость:
                        parseFloat(inv.amount) || 0,
                    'Дата создания': task.news_date,
                    'Дата появления': formatCustomDate(
                        appearanceDate
                    ),
                    'Дата покидания':
                        formatCustomDate(exitDate),
                    '%': inv.percent || '-',
                    'Описание работ':
                        workDescription || '-',
                    'Кто заполнил ВДР':
                        task.filling_user_fio ||
                        task.filling_user_name ||
                        '-',
                    Примечание: inv.note || '',
                    Учитывать: invoiceIncluded(inv)
                        ? 'Да'
                        : 'Нет'
                };

                Object.keys(cf).forEach(key => {
                    row[`[Поле] ${key}`] =
                        cf[key]?.value ?? '-';
                });

                return row;
            });

            const ws = XLSX.utils.json_to_sheet(
                wsData,
                isShort ? { header: shortHeaders } : undefined
            );
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(
                wb,
                ws,
                isShort ? 'ВДР кратко' : 'Invoices'
            );

            const fileName = isShort
                ? `Ведомость_ВДР_кратко_${dayjs().format('DD_MM_YYYY_HHmm')}.xlsx`
                : `Ведомость_ВДР_${dayjs().format('DD_MM_YYYY_HHmm')}.xlsx`;

            XLSX.writeFile(wb, fileName);

            message.success(
                `Экспорт (${isShort ? 'сокращённый' : 'полный'}) завершён: ` +
                    `${list.length} строк, ` +
                    `${data.meta?.tasks_count || 0} задач`
            );
        } catch (error) {
            console.error('Export error:', error);
            message.error(
                error.response?.data?.message ||
                    'Ошибка при выгрузке ВДР'
            );
        } finally {
            hideLoading();
            setIsExporting(false);
        }
    };

    const fetchDictionaries = async () => {
        const token = localStorage.getItem('token');
        const [cp, nom, art, pt, sc] = await Promise.allSettled([
            axios.get('/api/counterparties?limit=1000', { headers: { 'Authorization': `Bearer ${token}` } }),
            axios.get('/api/nomenclature?limit=10000', { headers: { 'Authorization': `Bearer ${token}` } }),
            axios.get('/api/articles?limit=1000', { headers: { 'Authorization': `Bearer ${token}` } }),
            axios.get('/api/pricetypes?limit=1000', { headers: { 'Authorization': `Bearer ${token}` } }),
            axios.get('/api/scenarios?limit=1000', { headers: { 'Authorization': `Bearer ${token}` } }), // ДОБАВЛЕНО
        ]);
        if (cp.status === 'fulfilled') {
            setCounterparties(cp.value.data.data || []);
            if (cp.value.data.options?.articles) setFinancialArticles(cp.value.data.options.articles);
            if (cp.value.data.options?.priceTypes) setPriceTypesList(cp.value.data.options.priceTypes);
        }
        if (nom.status === 'fulfilled') {
            setNomenclatures(nom.value.data.data || []);
            if (nom.value.data.priceTypes && !priceTypesList.length) setPriceTypesList(nom.value.data.priceTypes);
        }
        if (art.status === 'fulfilled' && !financialArticles.length) setFinancialArticles(art.value.data.data || []);
        if (pt.status === 'fulfilled' && !priceTypesList.length) setPriceTypesList(pt.value.data.data || []);
        if (sc.status === 'fulfilled') {
            setAllScenarios(sc.value.data.data || []); // Сохраняем все сценарии
        }
    };

    const handleApplyManualScenario = () => {
        if (!manualScenarioId) return message.warning('Выберите сценарий из списка');
        const scenario = allScenarios.find(s => s.id === manualScenarioId);
        if (scenario) {
            applySelectedScenario(scenario);
        }
    };

    const openInvoiceModal = async (
    dbId,
    externalId,
    status
) => {
    setTaskClosedAt(null);
    setTaskClosedAtSaving(false);
    setGepardCheck(null);
    setGepardMismatchCodes(new Set());
    setGepardMismatchHints({});
    setGepardMissingInVdrCodes(new Set());
    setGepardModalOpen(false);

    setInvoices({
        income: [],
        expense: []
    });

    setChildTasks([]);
    setHasUnsavedChanges(false);
    setCommentHistory([]);

    editInvoiceForm.resetFields();

    setEditingInvoice(null);
    setExternalTaskData(null);
    setTaskComments('');
    setTaskHistory([]);

    setCurrentTaskId(
        externalId
    );

    setDbTaskId(
        dbId
    );

    setCurrentTaskStatus(
        status || 'new'
    );

    setModalIsOpen(true);
    setInvoiceLoading(true);

    try {
        await fetchDictionaries();

        const token =
            localStorage.getItem(
                'token'
            );

        const headers = {
            Authorization:
                `Bearer ${token}`
        };

        const [
            ext,
            inv,
            hist,
            comms,
            children
        ] = await Promise.all([
            axios.get(
                `/api/tasks/ext/${externalId}`,
                {
                    headers
                }
            ),

            axios.get(
                `/api/tasks/${dbId}/invoices`,
                {
                    headers
                }
            ),

            axios.get(
                `/api/tasks/${dbId}/history`,
                {
                    headers
                }
            ).catch(() => ({
                data: []
            })),

            axios.get(
                `/api/tasks/${dbId}/comments`,
                {
                    headers
                }
            ).catch(() => ({
                data: []
            })),

            axios.get(
                `/api/tasks/${externalId}/children`,
                {
                    headers
                }
            ).catch(() => ({
                data: []
            }))
        ]);

        setChildTasks(
            Array.isArray(children.data)
                ? children.data
                : []
        );

        setExternalTaskData(
            ext.data || null
        );

        setInvoiceLinkHistoryKey(key => key + 1);
        setInvoices({
            income:
                Array.isArray(
                    inv.data?.income
                )
                    ? inv.data.income
                    : [],

            expense:
                Array.isArray(
                    inv.data?.expense
                )
                    ? inv.data.expense
                    : []
        });

        setTaskHistory(
            Array.isArray(hist.data)
                ? hist.data
                : []
        );

        setCommentHistory(
            Array.isArray(comms.data)
                ? comms.data
                : []
        );

        const loadedClosedAt =
            inv.data?.closed_at;

        console.log(
            '📅 [FRONT CLOSED_AT]',
            {
                dbId,
                externalId,
                loadedClosedAt,
                response:
                    inv.data
            }
        );

        if (loadedClosedAt) {
            const parsedClosedAt =
                dayjs(
                    loadedClosedAt
                );

            console.log(
                '📅 [PARSED CLOSED_AT]',
                {
                    valid:
                        parsedClosedAt.isValid(),

                    formatted:
                        parsedClosedAt.isValid()
                            ? parsedClosedAt.format(
                                'DD.MM.YYYY HH:mm'
                            )
                            : null
                }
            );

            setTaskClosedAt(
                parsedClosedAt.isValid()
                    ? parsedClosedAt
                    : null
            );
        } else {
            setTaskClosedAt(null);
        }

        try {
            const gepardResponse =
                await axios.get(
                    `/api/gepard-mail/task/${dbId}`,
                    {
                        headers
                    }
                );

            applyGepardCheckToUi(
                gepardResponse.data?.check ||
                    null,
                {
                    openModal: false
                }
            );
        } catch (gepardError) {
            console.warn(
                'Gepard check load failed',
                gepardError
            );
        }
    } catch (error) {
        console.error(
            'Ошибка открытия ВДР:',
            error
        );

        message.error(
            'Ошибка загрузки данных задачи'
        );
    } finally {
        setInvoiceLoading(false);
    }
};

    const applyGepardCheckToUi =
        (
            check,
            {
                openModal = false
            } = {}
        ) => {
            setGepardCheck(check || null);

            const codes = new Set();
            const hints = {};
            const missingInVdr = new Set();

            const putHint = (code, item) => {
                if (!code) {
                    return;
                }

                const normalized =
                    normalizeWorkCode(code);

                if (!normalized) {
                    return;
                }

                codes.add(normalized);

                let hint =
                    item?.hint ||
                    null;

                if (!hint) {
                    if (
                        item?.type ===
                        'missing_in_email'
                    ) {
                        hint =
                            'Отсутствует в письме';
                    } else if (item?.email) {
                        const email =
                            item.email;
                        const head = [
                            email.code,
                            email.name
                        ]
                            .filter(Boolean)
                            .join(' ')
                            .trim();
                        hint =
                            email.quantity !=
                            null
                                ? `${head || normalized} · кол-во ${email.quantity}`
                                : head ||
                                  normalized;
                    }
                }

                if (hint) {
                    hints[normalized] = hint;
                }

                if (
                    item?.type ===
                    'missing_in_vdr'
                ) {
                    missingInVdr.add(
                        normalized
                    );
                }
            };

            (check?.discrepancies || []).forEach(
                item => {
                    putHint(item?.code, item);
                }
            );

            (
                check?.suggestions?.to_add ||
                []
            ).forEach(item => {
                putHint(item?.code, {
                    ...item,
                    type:
                        item?.type ||
                        'missing_in_vdr'
                });
            });

            (
                check?.suggestions?.to_remove ||
                []
            ).forEach(item => {
                putHint(item?.code, {
                    ...item,
                    type:
                        item?.type ||
                        'missing_in_email',
                    hint:
                        item?.hint ||
                        'Отсутствует в письме'
                });
            });

            (
                check?.suggestions?.to_change ||
                []
            ).forEach(item => {
                putHint(item?.code, item);
            });

            setGepardMismatchCodes(codes);
            setGepardMismatchHints(hints);
            setGepardMissingInVdrCodes(
                missingInVdr
            );

            if (openModal && check) {
                setGepardModalOpen(true);
            }
        };

    const loadGepardDemo = async () => {
        if (!dbTaskId) {
            return;
        }

        try {
            const token =
                localStorage.getItem('token');

            const response =
                await axios.get(
                    '/api/gepard-mail/demo',
                    {
                        params: {
                            taskId: dbTaskId
                        },
                        headers: {
                            Authorization:
                                `Bearer ${token}`
                        }
                    }
                );

            applyGepardCheckToUi(
                response.data?.check || null,
                {
                    openModal: false
                }
            );

            message.info(
                'Показан тест/пример письма Gepard'
            );
        } catch (error) {
            message.error(
                'Не удалось загрузить demo Gepard'
            );
        }
    };

    /*
     * Когда письмо закрытия/отмены уже есть,
     * а доход ВДР пустой или неполный — добавляем
     * строки из suggestions.to_add по номенклатуре.
     */
    const applyGepardAddSuggestions = () => {
        const toAdd =
            gepardCheck?.suggestions?.to_add ||
            [];

        if (!toAdd.length) {
            message.info(
                'В письме Gepard нет позиций для добавления'
            );

            return;
        }

        if (editingInvoice) {
            message.warning(
                'Сначала завершите редактирование строки'
            );

            return;
        }

        let customFields = {};

        try {
            const raw =
                externalTaskData?.custom_fields;

            customFields =
                typeof raw === 'string'
                    ? JSON.parse(raw || '{}')
                    : raw || {};
        } catch (parseError) {
            customFields = {};
        }

        const customerName =
            getDynamicValue(
                '<Zakazchik>',
                externalTaskData || {},
                customFields
            ) ||
            (typeof invoices.income?.[0]
                ?.counterparty === 'object'
                ? invoices.income[0].counterparty
                      ?.label
                : invoices.income?.[0]
                      ?.counterparty);

        if (!customerName) {
            message.error(
                'Не найден заказчик для доходных строк'
            );

            return;
        }

        const counterparty = counterparties.find(
            item =>
                item.name &&
                item.name.toLowerCase() ===
                    String(customerName)
                        .toLowerCase()
        );

        if (!counterparty) {
            message.error(
                `Контрагент «${customerName}» не найден`
            );

            return;
        }

        const incomeArticle =
            financialArticles.find(
                item => item.name === '1. Доход'
            ) ||
            financialArticles.find(
                item =>
                    item.name &&
                    item.name
                        .toLowerCase()
                        .includes('доход')
            );

        if (!incomeArticle) {
            message.error(
                'Статья «1. Доход» не найдена'
            );

            return;
        }

        const allowedArticles = safeParseIntArray(
            counterparty.allowed_articles
        );

        if (
            allowedArticles.length &&
            !allowedArticles.includes(
                incomeArticle.id
            )
        ) {
            message.error(
                `Статья «${incomeArticle.name}» не разрешена для ${counterparty.name}`
            );

            return;
        }

        const priceLists = safeParseIntArray(
            counterparty.price_lists
        );

        const existingCodes = new Set(
            (invoices.income || [])
                .filter(row => row.include !== false)
                .map(row =>
                    extractWorkCodeFromRate(
                        row.rate_name
                    )
                )
                .filter(Boolean)
        );

        const findNomenclature = (
            code,
            emailName
        ) => {
            const matches = nomenclatures.filter(
                item => {
                    if (
                        String(
                            item.name || ''
                        ).startsWith('СП_')
                    ) {
                        return false;
                    }

                    return (
                        extractWorkCodeFromRate(
                            item.name
                        ) === code
                    );
                }
            );

            const inIncomeArticle = matches.filter(
                item =>
                    item.article_id ===
                    incomeArticle.id
            );

            const pool =
                inIncomeArticle.length > 0
                    ? inIncomeArticle
                    : matches;

            if (!pool.length) {
                return null;
            }

            if (pool.length === 1) {
                return pool[0];
            }

            const needle = String(emailName || '')
                .toLowerCase()
                .trim();

            if (needle) {
                const byName = pool.find(item => {
                    const name = String(
                        item.name || ''
                    ).toLowerCase();

                    return (
                        name.includes(
                            needle.slice(0, 40)
                        ) ||
                        needle.includes(
                            name.slice(0, 40)
                        )
                    );
                });

                if (byName) {
                    return byName;
                }
            }

            return pool[0];
        };

        const pickPrice = nomenclature => {
            if (
                !nomenclature?.prices ||
                typeof nomenclature.prices !==
                    'object'
            ) {
                return null;
            }

            for (const [
                priceTypeId,
                value
            ] of Object.entries(
                nomenclature.prices
            )) {
                const id = parseInt(
                    priceTypeId,
                    10
                );

                if (
                    priceLists.includes(id) &&
                    typeof value === 'number' &&
                    value > 0
                ) {
                    return value;
                }
            }

            for (const value of Object.values(
                nomenclature.prices
            )) {
                if (
                    typeof value === 'number' &&
                    value > 0
                ) {
                    return value;
                }
            }

            return 0;
        };

        const penaltyPercent =
            Number(gepardCheck?.penalty_percent) ||
            0;

        const newRows = [];
        const warnings = [];
        const addedCodes = [];

        for (const suggestion of toAdd) {
            const code = normalizeWorkCode(
                suggestion.code ||
                    suggestion.email?.code
            );

            if (!code) {
                continue;
            }

            if (existingCodes.has(code)) {
                continue;
            }

            const email = suggestion.email || {};
            const nomenclature = findNomenclature(
                code,
                email.name || suggestion.message
            );

            if (!nomenclature) {
                warnings.push(
                    `${code}: нет номенклатуры в справочнике`
                );
                continue;
            }

            const price = pickPrice(nomenclature);

            if (price == null) {
                warnings.push(
                    `${code}: нет цены для ${counterparty.name}`
                );
                continue;
            }

            const quantity =
                Number(
                    email.effective_quantity !=
                        null
                        ? email.effective_quantity
                        : email.quantity
                ) || 0;

            newRows.push({
                id: `new-${Date.now()}-${Math.random()
                    .toString(36)
                    .substr(2, 9)}`,
                type: 'income',
                counterparty: counterparty.name,
                article: incomeArticle.name,
                rate_name: nomenclature.name,
                unit: nomenclature.unit || '',
                price,
                quantity,
                percent: 100,
                note: '',
                penalty: penaltyPercent,
                include: true,
                amount: mathRound(
                    price * quantity
                )
            });

            existingCodes.add(code);
            addedCodes.push(code);
        }

        if (!newRows.length) {
            message.warning(
                warnings.length
                    ? `Ничего не добавлено: ${warnings.join('; ')}`
                    : 'Все предложенные коды уже есть в ВДР'
            );

            return;
        }

        setInvoices(prev => ({
            ...prev,
            income: [
                ...newRows,
                ...(prev.income || [])
            ]
        }));

        setHasUnsavedChanges(true);

        setGepardMissingInVdrCodes(prev => {
            const next = new Set(prev);

            addedCodes.forEach(code =>
                next.delete(code)
            );

            return next;
        });

        message.success(
            `Добавлено в доход: ${newRows.length} поз. Сохраните ВДР.`
        );

        if (warnings.length) {
            notification.warning({
                message:
                    'Часть позиций Gepard не добавлена',
                description: (
                    <div
                        style={{
                            maxHeight: 200,
                            overflow: 'auto'
                        }}
                    >
                        {warnings.map(
                            (text, index) => (
                                <div key={index}>
                                    • {text}
                                </div>
                            )
                        )}
                    </div>
                ),
                duration: 10
            });
        }
    };

    const openGepardDiscrepancy =
        async (
            checkId,
            {
                openVdr = true
            } = {}
        ) => {
            try {
                const token =
                    localStorage.getItem(
                        'token'
                    );

                const response =
                    await axios.get(
                        `/api/gepard-mail/${checkId}`,
                        {
                            headers: {
                                Authorization:
                                    `Bearer ${token}`
                            }
                        }
                    );

                const check =
                    response.data?.check;

                const task =
                    response.data?.task;

                applyGepardCheckToUi(check, {
                    openModal: true
                });

                if (
                    openVdr &&
                    task?.id
                ) {
                    await openInvoiceModal(
                        task.id,
                        task.external_id,
                        task.status_invoices
                    );
                }
            } catch (error) {
                console.error(
                    'Gepard check open failed',
                    error
                );

                message.error(
                    'Не удалось открыть расхождения Gepard'
                );
            }
        };

    useEffect(() => {
        const params =
            new URLSearchParams(
                location.search
            );

        const taskId =
            params.get('taskId');

        const gepardCheckId =
            params.get('gepardCheckId');

        if (!gepardCheckId) {
            return;
        }

        // _n с уведомления позволяет
        // повторно открыть ту же сверку
        const handleKey =
            `${taskId || ''}:${gepardCheckId}:${
                params.get('_n') || ''
            }`;

        if (
            gepardHandledRef.current ===
            handleKey
        ) {
            return;
        }

        gepardHandledRef.current =
            handleKey;

        openGepardDiscrepancy(
            gepardCheckId,
            {
                openVdr: true
            }
        ).finally(() => {
            params.delete('gepardCheckId');
            params.delete('taskId');
            params.delete('_n');

            const next =
                params.toString();

            navigate(
                {
                    pathname:
                        location.pathname,
                    search:
                        next
                            ? `?${next}`
                            : ''
                },
                {
                    replace: true
                }
            );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        location.search
    ]);

    const updateTaskClosedAt =
    async value => {
        if (
            !dbTaskId ||
            taskClosedAtSaving
        ) {
            return;
        }

        const previousValue =
            taskClosedAt;

        /*
         * Интерфейс обновляем сразу.
         * Если сервер вернёт ошибку — откатим.
         */
        setTaskClosedAt(
            value || null
        );

        setTaskClosedAtSaving(
            true
        );

        try {
            const token =
                localStorage.getItem(
                    'token'
                );

            const response =
                await axios.put(
                    `/api/tasks/${dbTaskId}/closed-at`,
                    {
                        closed_at:
                            value
                                ? value.toISOString()
                                : null
                    },
                    {
                        headers: {
                            Authorization:
                                `Bearer ${token}`
                        }
                    }
                );

            setTaskClosedAt(
                response.data?.closed_at
                    ? dayjs(
                        response.data.closed_at
                    )
                    : null
            );

            /*
             * Сервер вернул созданную запись истории.
             * Добавляем её без дополнительного HTTP-запроса.
             */
            if (
                response.data?.history
            ) {
                setTaskHistory(
                    previous => [
                        response.data.history,
                        ...previous
                    ]
                );
            }

            message.success(
                value
                    ? 'Дата закрытия сохранена'
                    : 'Дата закрытия очищена'
            );
        } catch (error) {
            /*
             * Откатываем DatePicker к прежнему значению.
             */
            setTaskClosedAt(
                previousValue
            );

            console.error(
                'Ошибка сохранения даты закрытия:',
                error
            );

            message.error(
                error.response?.data?.error ||
                'Не удалось сохранить дату закрытия'
            );
        } finally {
            setTaskClosedAtSaving(
                false
            );
        }
    };

    const handleCopyImmediate = (type, record) => {
        const newId = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newRow = { ...record, id: newId };
        setInvoices(prev => ({ ...prev, [type]: [newRow, ...prev[type]] }));
        message.success('Строка скопирована');
    };

    const handleAddNewInlineInitiate = (type) => {
        if (editingInvoice) return message.warning('Завершите редактирование');
        const tempId = `new-${Date.now()}`;
        const emptyRow = { id: tempId, type, quantity: 1, percent: 100, penalty: 0, include: true, price: 0, rate_name: '', unit: '', counterparty: null, article: null };
        setHasUnsavedChanges(true);
        setInvoices(prev => ({ ...prev, [type]: [emptyRow, ...prev[type]] }));
        startEditingInvoice(emptyRow, type);
    };

    /*const startEditingInvoice = (inv, type) => {
        if (editingInvoice) return message.warning('Завершите редактирование');
        setEditingInvoice({ id: inv.id, type });
        const cp = _getCounterpartyOptionByName(typeof inv.counterparty === 'object' ? inv.counterparty.label : inv.counterparty, counterparties);
        const art = cp ? _getArticleOptionByName(cp, typeof inv.article === 'object' ? inv.article.label : inv.article, counterparties, financialArticles) : null;
        const rate = (cp && art) ? _getNomenclatureOptionByDetails(cp, art, inv.rate_name, inv.unit, inv.price, counterparties, nomenclatures, priceTypesList, false) : null;

        editInvoiceForm.setFieldsValue({
            ...inv,
            counterparty: cp,
            article: art,
            selectedRateOption: rate,
            rate_name: inv.rate_name,
            price: inv.price,
            unit: inv.unit,
            quantity: inv.quantity || 1,
            percent: inv.percent || 100
        });
    };*/
    const startEditingInvoice = (inv, type) => {
        if (inv?.isChild) {
            message.info(
                'Строка дочерней задачи доступна только для чтения'
            );

            return;
        }

        if (isInvoiceLocked(inv)) {
            message.warning(
                `Строка уже включена в документ: ${
                    inv
                        .linked_registry_numbers ||
                    'документ не указан'
                }`
            );

            return;
        }

        if (editingInvoice) {
            message.warning(
                'Завершите редактирование текущей строки'
            );

            return;
        }
        if (inv.isChild) return;
        setEditingInvoice({ id: inv.id, type });
        
        const cp = _getCounterpartyOptionByName(typeof inv.counterparty === 'object' ? inv.counterparty.label : inv.counterparty, counterparties);
        const art = cp ? _getArticleOptionByName(cp, typeof inv.article === 'object' ? inv.article.label : inv.article, counterparties, financialArticles) : null;
        
        // exactPriceMatch = false: найти прайс даже если цену потом правили вручную.
        // Заказчик нужен, чтобы при нескольких прайсах не подставлять первый в списке.
        let customerName = null;
        try {
            const fields = externalTaskData?.custom_fields
                ? (typeof externalTaskData.custom_fields === 'string' ? JSON.parse(externalTaskData.custom_fields) : externalTaskData.custom_fields)
                : {};
            customerName = externalTaskData ? getDynamicValue('<Zakazchik>', externalTaskData, fields) : null;
        } catch (_) {
            customerName = null;
        }
        const rate = (cp && art) ? _getNomenclatureOptionByDetails(cp, art, inv.rate_name, inv.unit, inv.price, counterparties, nomenclatures, priceTypesList, false, customerName) : null;

        editInvoiceForm.setFieldsValue({
            ...inv,
            counterparty: cp,
            article: art,
            // Сохраняем найденную расценку (она содержит оригинальный priceValue из прайса)
            selectedRateOption: rate,
            rate_name: inv.rate_name,
            price: inv.price,
            unit: inv.unit,
            quantity: inv.quantity || 1,
            percent: parsePercent(inv.percent, 100),
            penalty: inv.penalty || 0
        });
    };
    const renderParentTask = () => {
        const parent = externalTaskData?.parent;
        if (!parent || !parent.id) return null;

        return (
            <Col span={24} style={{ marginTop: '4px', borderTop: '1px dashed #d9d9d9', paddingTop: '4px' }}>
                <Text strong>Родительская задача: </Text>
                <a
                    href={`https://smartcomservice.geofsm.ru/tasks/#task/${parent.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    #{parent.id}
                </a>
                <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                    {parent.title}
                </Text>
            </Col>
        );
    };
    const renderChildTasks = () => {
        if (!childTasks || childTasks.length === 0) return null;

        return (
            <Col span={24} style={{ marginTop: '4px', borderTop: '1px dashed #d9d9d9', paddingTop: '4px' }}>
                <Text strong>Дочерние задачи: </Text>
                <Space wrap>
                    {childTasks.map(child => (
                        <Tag color="blue" key={child.external_id}>
                            <a
                                href={`https://smartcomservice.geofsm.ru/tasks/#task/${child.external_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                #{child.external_id}
                            </a>
                        </Tag>
                    ))}
                </Space>
            </Col>
        );
    };
    /*const saveEditedInvoice = async () => {
        try {
            const formValues = await editInvoiceForm.validateFields();
            const finalRateName = formValues.rate_name || formValues.selectedRateOption?.name || editingInvoice.rate_name;
            

            setInvoices(prev => ({
                ...prev,
                [editingInvoice.type]: prev[editingInvoice.type].map(i => i.id === editingInvoice.id ? {
                    ...i,
                    counterparty: formValues.counterparty.label || formValues.counterparty,
                    article: formValues.article.label || formValues.article,
                    rate_name: finalRateName,
                    unit: formValues.unit,
                    price: formValues.price,
                    quantity: formValues.quantity,
                    percent: formValues.percent,
                    note: formValues.note,
                    penalty: formValues.penalty,
                    include: formValues.include !== undefined ? formValues.include : i.include,
                    amount: mathRound(formValues.price * formValues.quantity * (formValues.percent / 100))
                } : i)
            }));
            setHasUnsavedChanges(true);
            setEditingInvoice(null);
            editInvoiceForm.resetFields();
        } catch (e) { message.error('Ошибка валидации'); }
    };*/
    const saveEditedInvoice = async () => {
        try {
            const formValues = await editInvoiceForm.validateFields();
            const finalRateName = formValues.rate_name || formValues.selectedRateOption?.name || editingInvoice.rate_name;

            // НОВАЯ ЛОГИКА РАСЧЕТА
            const baseAmount = formValues.price * formValues.quantity * (formValues.percent / 100);
            const amountAfterPenalty = mathRound(baseAmount * (1 - formValues.penalty / 100));

            setInvoices(prev => ({
                ...prev,
                [editingInvoice.type]: prev[editingInvoice.type].map(i => i.id === editingInvoice.id ? {
                    ...i,
                    counterparty: formValues.counterparty.label || formValues.counterparty,
                    article: formValues.article.label || formValues.article,
                    rate_name: finalRateName,
                    unit: formValues.unit,
                    price: formValues.price,
                    quantity: formValues.quantity,
                    percent: formValues.percent,
                    note: formValues.note,
                    penalty: formValues.penalty, // Сохраняем процент штрафа
                    include: formValues.include !== undefined ? formValues.include : i.include,
                    amount: amountAfterPenalty // Записываем итоговую сумму (может быть отрицательной)
                } : i)
            }));
            setHasUnsavedChanges(true);
            setEditingInvoice(null);
            editInvoiceForm.resetFields();
        } catch (e) { message.error('Ошибка валидации'); }
    };

    const cancelEditing = () => {
        if (editingInvoice && editingInvoice.id.toString().startsWith('new')) {
            deleteInvoice(editingInvoice.type, editingInvoice.id);
        }
        setEditingInvoice(null);
        editInvoiceForm.resetFields();
    };

    const renderTotalRoadTime = () => {
        if (!externalTaskData?.custom_fields) return null;
        try {
            // Проверяем, не является ли custom_fields уже объектом (иногда axios парсит его сам)
            const cf = typeof externalTaskData.custom_fields === 'string'
                ? JSON.parse(externalTaskData.custom_fields)
                : externalTaskData.custom_fields;

            const to = parseInt(cf['Doroga_do_obiekta_(minut)']?.value) || 0;
            const from = parseInt(cf['Doroga_s_obiekta_(minut)']?.value) || 0;
            const total = to + from;

            // Если данных нет вообще, можем ничего не выводить или вывести 0
            if (total === 0) return <Col span={12}><Text strong>Итого дорога:</Text> 0 мин</Col>;

            return (
                <Col span={12}>
                    <Text strong>Итого дорога:</Text> <Text type="primary" weight="bold">{total} мин</Text>
                </Col>
            );
        } catch (e) {
            console.error("Ошибка расчета времени:", e);
            return null;
        }
    };
    const saveInvoiceChanges = async () => {
        if (editingInvoice) {
            message.warning(
                'Сначала завершите редактирование строки'
            );

            return;
        }

        setIsSaving(true);

        const hide = message.loading(
            'Сохранение изменений...',
            0
        );

        try {
            const token =
                localStorage.getItem('token');

            const headers = {
                Authorization:
                    `Bearer ${token}`
            };

            const prepare = list =>
                list.map(item => ({
                    ...item,

                    counterparty:
                        typeof item.counterparty ===
                        'object'
                            ? item.counterparty.label
                            : item.counterparty,

                    article:
                        typeof item.article ===
                        'object'
                            ? item.article.label
                            : item.article
                }));

            await axios.put(
                `/api/tasks/${dbTaskId}/invoices`,
                {
                    income:
                        prepare(invoices.income),

                    expense:
                        prepare(invoices.expense),

                    comments:
                        taskComments
                },
                {
                    headers
                }
            );

            /*
            * Повторно получаем строки вместе
            * с is_locked и linked_registries.
            */
            const refreshed =
                await axios.get(
                    `/api/tasks/${dbTaskId}/invoices`,
                    {
                        headers
                    }
                );

            setInvoiceLinkHistoryKey(key => key + 1);
            setInvoices({
                income:
                    refreshed.data?.income || [],

                expense:
                    refreshed.data?.expense || []
            });

            setTaskComments(
                refreshed.data?.comments || ''
            );

            const refreshedClosedAt =
                refreshed.data?.closed_at;

            if (refreshedClosedAt) {
                const parsedClosedAt =
                    dayjs(refreshedClosedAt);

                setTaskClosedAt(
                    parsedClosedAt.isValid()
                        ? parsedClosedAt
                        : null
                );
            }

            setHasUnsavedChanges(false);

            await fetchTaskMetadata(
                dbTaskId
            );

            try {
                const gepardResponse =
                    await axios.get(
                        `/api/gepard-mail/task/${dbTaskId}`,
                        {
                            headers
                        }
                    );

                applyGepardCheckToUi(
                    gepardResponse.data?.check ||
                        null,
                    {
                        openModal: false
                    }
                );
            } catch (gepardError) {
                console.warn(
                    'Gepard recheck after save failed',
                    gepardError
                );
            }

            message.success(
                'ВДР успешно сохранён'
            );
        } catch (error) {
            console.error(
                'Ошибка сохранения ВДР:',
                error
            );

            if (
                error.response?.status === 409 &&
                error.response?.data?.code ===
                    'INVOICES_LINKED_TO_REGISTRIES'
            ) {
                const blocked =
                    error.response.data
                        .blocked_deletions || [];

                Modal.error({
                    title:
                        'Нельзя удалить строки',

                    width: 600,

                    content: (
                        <div>
                            {blocked.map(item => (
                                <div
                                    key={
                                        item.invoice_id
                                    }
                                    style={{
                                        marginBottom: 10
                                    }}
                                >
                                    <b>
                                        ID{' '}
                                        {
                                            item.invoice_id
                                        }
                                        :{' '}
                                        {
                                            item.rate_name
                                        }
                                    </b>

                                    <div>
                                        Документы:{' '}
                                        {item.registries
                                            ?.map(
                                                (registry, index) => (
                                                    <React.Fragment
                                                        key={registry.id}
                                                    >
                                                        {index > 0 && ', '}
                                                        {renderRegistryLink(
                                                            registry
                                                        )}
                                                    </React.Fragment>
                                                )
                                            )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                });

                return;
            }

            message.error(
                error.response?.data?.message ||
                'Ошибка сохранения ВДР'
            );
        } finally {
            hide();
            setIsSaving(false);
        }
    };

    const deleteInvoice = (type, id) => {
        const invoice =
            invoices[type]?.find(
                item =>
                    String(item.id) ===
                    String(id)
            );

        if (isInvoiceLocked(invoice)) {
            message.warning(
                `Строка используется в документе: ${
                    invoice
                        .linked_registry_numbers ||
                    ''
                }`
            );

            return;
        }

        setInvoices(prev => ({ ...prev, [type]: prev[type].filter(item => item.id !== id) }));
        setHasUnsavedChanges(true);
        if (editingInvoice && editingInvoice.id === id) { setEditingInvoice(null); editInvoiceForm.resetFields(); }
    };

    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination(newPagination);

        setApiFilters(prev => ({
            ...prev,
            // Сохраняем значения из фильтров колонок (если кликнули там)
            // или оставляем старые (если кликнули в верхнем блоке)
            news_type_name: filters.news_type_name || prev.news_type_name,
            onlyStatus: filters.onlyStatus || prev.onlyStatus,
            // Обработка статуса ВДР (преобразуем массив в строку для вашего API)
            status_invoices: filters.status_invoices ? filters.status_invoices : prev.status_invoices,

            // Сортировка
            sort: sorter.field ? (Array.isArray(sorter.field) ? sorter.field.join('.') : sorter.field) : undefined,
            order: sorter.order === 'ascend' ? 'ASC' : (sorter.order === 'descend' ? 'DESC' : undefined)
        }));
    };

    const renderCustomField = (field, label, suffix = '') => {
        if (!externalTaskData?.custom_fields) return null;
        try {
            const cf = typeof externalTaskData.custom_fields === 'string'
                ? JSON.parse(externalTaskData.custom_fields)
                : externalTaskData.custom_fields;

            let val = cf[field]?.value;

            if (val === undefined || val === null || val === '' || val === 'false') {
                return <Col span={12}><Text strong>{label}:</Text> -</Col>;
            }

            // Если поле содержит информацию о времени
            if (field.toLowerCase().includes('vremja') || field.toLowerCase().includes('srok')) {
                const isNumeric = /^\d+$/.test(val.toString().trim());

                if (isNumeric) {
                    const num = val.toString().length === 10 ? Number(val) * 1000 : Number(val);
                    let date = dayjs(num);
                    // Коррекция под МСК
                    const pcOffset = date.utcOffset();
                    const mskOffset = 180;
                    date = date.add(mskOffset - pcOffset, 'minute');
                    val = date.format('DD.MM.YYYY HH:mm');
                } else {
                    const parsedDate = dayjs(val, "DD.MM.YYYY HH:mm:ss");
                    if (parsedDate.isValid()) {
                        val = parsedDate.format('DD.MM.YYYY HH:mm');
                    } else {
                        // Если строка не парсится (например, DD.MM.YYYY), просто берем первые 16 символов
                        val = val.toString().substring(0, 16);
                    }
                }
            }

            return <Col span={12}><Text strong>{label}:</Text> {val}{suffix}</Col>;
        } catch (e) {
            return <Col span={12}><Text strong>{label}:</Text> -</Col>;
        }
    };
    const renderField = (label, value) => <Col span={12}><Text strong>{label}:</Text> {value || '-'}</Col>;

    const isInvoiceLocked =
    record => {
        if (!record) {
            return false;
        }

        /*
         * Новый backend уже возвращает
         * список только блокирующих документов.
         */
        if (
            Array.isArray(
                record.blocking_registries
            )
        ) {
            return (
                record.blocking_registries
                    .length > 0
            );
        }

        /*
         * Совместимость со старым ответом API:
         * исключаем daily_report прямо на фронте.
         */
        const blockingRegistries =
            Array.isArray(
                record.linked_registries
            )
                ? record.linked_registries
                    .filter(
                        registry =>
                            registry?.type !==
                            'daily_report'
                    )
                : [];

        return (
            blockingRegistries.length >
            0
        );
    };

    const getRegistryTypeLabel = type => {
        const labels = {
            customer_order:
                'Заказ покупателя',

            customer_act:
                'Акт покупателя',

            supplier_order:
                'Заказ поставщика',

            supplier_act:
                'Акт поставщика',

            daily_report:
                'Ежедневный отчёт'
        };

        return labels[type] || type || 'Документ';
    };

    const getRegistryUrl = registry =>
        registry?.type === 'daily_report'
            ? `/daily-reports?id=${registry.id}`
            : `/docs?id=${registry?.id}`;

    const renderRegistryLink = (registry, children, style) => (
        <a
            href={getRegistryUrl(registry)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={event => event.stopPropagation()}
            style={style}
        >
            {children ||
                registry.number ||
                `ID ${registry.id}`}
        </a>
    );

    const invoiceTableColumns = useCallback((form, type) => {
        const commonStyle = { width: '100%', padding: '4px' };
        return [
            {
                title: 'Действия',
                width: 130,
                fixed: 'left',
                align: 'center',

                render: (_, r) => {
                    if (r.isChild) {
                        return (
                            <div
                                style={{
                                    lineHeight: '1.2'
                                }}
                            >
                                <Tag
                                    color="blue"
                                    style={{
                                        margin: 0,
                                        fontSize: '10px'
                                    }}
                                >
                                    Дочерняя
                                </Tag>

                                <div
                                    style={{
                                        fontSize: '10px',
                                        marginTop: '4px'
                                    }}
                                >
                                    #{r.childExternalId}
                                </div>
                            </div>
                        );
                    }

                    const isEdit =
                        editingInvoice?.id === r.id;

                    const isLocked =
                        isInvoiceLocked(r);

                    let actionButtons;

                    /*
                    * Заблокированная строка:
                    * можно только копировать.
                    */
                    if (isLocked) {
                        actionButtons = (
                            <Space size="small">
                                <Button
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={() =>
                                        handleCopyImmediate(
                                            type,
                                            r
                                        )
                                    }
                                    disabled={
                                        Boolean(
                                            editingInvoice
                                        )
                                    }
                                />
                            </Space>
                        );
                    } else if (isEdit) {
                        actionButtons = (
                            <Space size="small">
                                <Button
                                    size="small"
                                    icon={<SaveOutlined />}
                                    type="primary"
                                    onClick={
                                        saveEditedInvoice
                                    }
                                />

                                <Button
                                    size="small"
                                    icon={<CloseOutlined />}
                                    onClick={
                                        cancelEditing
                                    }
                                />
                            </Space>
                        );
                    } else {
                        actionButtons = (
                            <Space size="small">
                                <Button
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={() =>
                                        handleCopyImmediate(
                                            type,
                                            r
                                        )
                                    }
                                    disabled={
                                        Boolean(
                                            editingInvoice
                                        )
                                    }
                                />

                                <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() =>
                                        startEditingInvoice(
                                            r,
                                            type
                                        )
                                    }
                                    disabled={
                                        Boolean(
                                            editingInvoice
                                        )
                                    }
                                />

                                <Popconfirm
                                    title="Удалить?"
                                    onConfirm={() =>
                                        deleteInvoice(
                                            type,
                                            r.id
                                        )
                                    }
                                >
                                    <Button
                                        size="small"
                                        icon={
                                            <DeleteOutlined />
                                        }
                                        danger
                                        disabled={
                                            Boolean(
                                                editingInvoice
                                            )
                                        }
                                    />
                                </Popconfirm>
                            </Space>
                        );
                    }

                    return (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                lineHeight: '1'
                            }}
                        >
                            {actionButtons}

                            <span
                                style={{
                                    fontSize: '10px',
                                    color: '#221f1f',
                                    marginTop: '4px',
                                    userSelect: 'none'
                                }}
                            >
                                {String(r.id).startsWith(
                                    'new'
                                )
                                    ? 'NEW'
                                    : `ID: ${r.id}`}
                            </span>
                        </div>
                    );
                }
            },
            { 
                title: 'Контрагент', 
                width: 200, 
                render: (_, r) => (editingInvoice?.id === r.id) ? (
                    // ФИЛЬТРАЦИЯ ТУТ:
                    <Form.Item name="counterparty" rules={[{required:true, message:'!'}]} style={{marginBottom:0}}>
                        <Select 
                            autoFocus
                            options={
                                type === 'income' 
                                    ? counterparties.filter(c => c.type?.toLowerCase() === 'customer').map(c => ({ value: c.id, label: c.name }))
                                    : counterparties.map(c => ({ value: c.id, label: c.name }))
                            } 
                            labelInValue 
                            showSearch 
                            filterOption={(i,o)=>o.label.toLowerCase().includes(i.toLowerCase())} 
                            onChange={v => form.setFieldsValue({counterparty:v, article:null, selectedRateOption:null, price:0})} 
                        />
                    </Form.Item>
                ) : (typeof r.counterparty === 'object' ? r.counterparty.label : r.counterparty)
            },
            { title: 'Статья', width: 180, render: (_, r) => (editingInvoice?.id === r.id) ?
                    <Form.Item noStyle shouldUpdate={(p,c) => p.counterparty !== c.counterparty}>
                        {({getFieldValue}) => <FixedArticleSelectCell formInstance={form} rules={[{required:true, message:'!'}]} memoizedGetArticleOptions={memoizedGetArticleOptions} currentCounterparty={getFieldValue('counterparty')} />}
                    </Form.Item> : r.article
            },
            {
                title: 'Наименование',
                width: 300,
                render: (_, r) => {
                    if (editingInvoice?.id === r.id) {
                        return (
                            <Form.Item
                                noStyle
                                shouldUpdate={(
                                    p,
                                    c
                                ) =>
                                    p.article !==
                                    c.article
                                }
                            >
                                {({
                                    getFieldValue
                                }) => (
                                    <FixedNomenclatureSelectCell
                                        formInstance={
                                            form
                                        }
                                        rules={[
                                            {
                                                required: true,
                                                message: '!'
                                            }
                                        ]}
                                        memoizedGetNomenclatureOptions={
                                            memoizedGetNomenclatureOptions
                                        }
                                        currentCounterparty={getFieldValue(
                                            'counterparty'
                                        )}
                                        currentArticle={getFieldValue(
                                            'article'
                                        )}
                                    />
                                )}
                            </Form.Item>
                        );
                    }

                    const code =
                        type === 'income'
                            ? extractWorkCodeFromRate(
                                  r.rate_name
                              )
                            : '';
                    const gepardHint =
                        code &&
                        gepardMismatchHints[code]
                            ? gepardMismatchHints[
                                  code
                              ]
                            : null;

                    return (
                        <div>
                            <div>
                                {r.rate_name}
                            </div>
                            {gepardHint ? (
                                <div
                                    style={{
                                        marginTop: 4,
                                        fontSize: 11,
                                        lineHeight: 1.35,
                                        color:
                                            gepardHint ===
                                            'Отсутствует в письме'
                                                ? '#cf1322'
                                                : '#ad6800',
                                        whiteSpace:
                                            'normal'
                                    }}
                                >
                                    Gepard:{' '}
                                    {gepardHint}
                                </div>
                            ) : null}
                        </div>
                    );
                }
            },
            { title: 'Кол-во', width: 100, align: 'right', render: (_, r) => (editingInvoice?.id === r.id) ?
                    <Form.Item name="quantity" rules={[{required:true}]} style={{marginBottom:0}}>
                        <InputNumber min={0} step={1} decimalSeparator="," style={commonStyle} onPressEnter={saveEditedInvoice} />
                    </Form.Item> : r.quantity
            },
            { title: 'Цена', width: 110, align: 'right', render: (_, r) => (editingInvoice?.id === r.id) ?
                    <EditablePriceInput name="price" formInstance={form} commonInputStyle={commonStyle} rules={[{required:true}]} onEnter={saveEditedInvoice} /> : r.price?.toLocaleString('ru-RU')
            },
            { title: 'Ед.', width: 60, render: (_, r) => (editingInvoice?.id === r.id) ? <Form.Item name="unit" style={{marginBottom:0}}><Input readOnly tabIndex={-1} style={{...commonStyle, background:'#f0f0f0'}} /></Form.Item> : r.unit },
            { title: '%', width: 90, align: 'right', render: (_, r) => (editingInvoice?.id === r.id) ?
                    <Form.Item name="percent" style={{marginBottom:0}}>
                        <InputNumber
                            {...percentInputNumberProps}
                            style={commonStyle}
                            onPressEnter={saveEditedInvoice}
                        />
                    </Form.Item> : `${formatPercent(r.percent)}%`
            },
            { title: 'Сумма', width: 110, align: 'right', render: (_, r) => (editingInvoice?.id === r.id) ?
                    <CalculatedAmountDisplay formInstance={form} commonInputStyle={commonStyle} /> : r.amount?.toLocaleString('ru-RU', {minimumFractionDigits: 2})
            },
            { title: 'Примечание', width: 150, render: (_, r) => (editingInvoice?.id === r.id) ?
                    <Form.Item name="note" style={{marginBottom:0}}><Input style={commonStyle} onPressEnter={saveEditedInvoice} /></Form.Item> : r.note
            },
            { 
                title: 'Штраф %', // Добавили % в заголовок
                width: 90, 
                align: 'right', 
                render: (_, r) => (editingInvoice?.id === r.id) ?
                    <Form.Item name="penalty" style={{marginBottom:0}} initialValue={0}>
                        <InputNumber 
                            min={0} 
                            max={1000} // Позволяем большие значения
                            step={1}
                            
                            formatter={value => `${value}%`}
                            parser={value => value.replace('%', '')}
                            style={commonStyle} 
                            onPressEnter={saveEditedInvoice} 
                        />
                    </Form.Item> : `${r.penalty || 0}%`
            },
            {
                title: 'Учит.',
                width: 70,
                align: 'center',

                render: (_, record) => {
                    const locked =
                        record.isChild ||
                        isInvoiceLocked(record);

                    return (
                        <Tooltip
                            title={
                                locked
                                    ? record
                                        .linked_registry_numbers ||
                                    'Строка доступна только для чтения'
                                    : null
                            }
                        >
                            <span>
                                <Switch
                                    checked={
                                        Boolean(
                                            record.include
                                        )
                                    }
                                    disabled={
                                        locked ||
                                        Boolean(
                                            editingInvoice
                                        )
                                    }
                                    onChange={value => {
                                        if (locked) {
                                            return;
                                        }

                                        setInvoices(prev => ({
                                            ...prev,

                                            [type]:
                                                prev[type].map(
                                                    item =>
                                                        item.id ===
                                                        record.id
                                                            ? {
                                                                ...item,
                                                                include:
                                                                    value
                                                            }
                                                            : item
                                                )
                                        }));

                                        setHasUnsavedChanges(
                                            true
                                        );
                                    }}
                                />
                            </span>
                        </Tooltip>
                    );
                }
            },
            {
                title: 'Документы',
                key: 'linked_registries',
                width: 230,
                fixed: 'left',

                render: (_, record) => {
                    if (record.isChild) {
                        return (
                            <Tag color="blue">
                                Дочерняя задача
                            </Tag>
                        );
                    }

                    const registries =
                        Array.isArray(
                            record.linked_registries
                        )
                            ? record.linked_registries
                            : [];

                    if (registries.length === 0) {
                        return (
                            <Tag color="green">
                                Свободна
                            </Tag>
                        );
                    }

                    const visible =
                        registries.slice(0, 2);

                    const hiddenCount =
                        Math.max(
                            registries.length -
                            visible.length,
                            0
                        );

                    return (
                        <Tooltip
                            placement="topLeft"
                            title={
                                <div>
                                    <div
                                        style={{
                                            fontWeight: 700,
                                            marginBottom: 6
                                        }}
                                    >
                                        Редактирование заблокировано
                                    </div>

                                    {registries.map(registry => (
                                        <div
                                            key={registry.id}
                                            style={{
                                                marginBottom: 4
                                            }}
                                        >
                                            {getRegistryTypeLabel(
                                                registry.type
                                            )}
                                            :{' '}
                                            {renderRegistryLink(
                                                registry,
                                                null,
                                                {
                                                    fontWeight: 700,
                                                    color: '#69b1ff',
                                                    textDecoration: 'underline'
                                                }
                                            )}

                                            {registry.parent_number
                                                ? `, основание ${registry.parent_number}`
                                                : ''}
                                        </div>
                                    ))}
                                </div>
                            }
                        >
                            <Space
                                size={[4, 4]}
                                wrap
                            >
                                {visible.map(registry => (
                                    <Tag
                                        key={registry.id}
                                        color={
                                            registry.type
                                                ?.includes(
                                                    'act'
                                                )
                                                ? 'cyan'
                                                : 'orange'
                                        }
                                    >
                                        {renderRegistryLink(
                                            registry,
                                            null,
                                            {
                                                color: 'inherit',
                                                textDecoration: 'underline'
                                            }
                                        )}
                                    </Tag>
                                ))}

                                {hiddenCount > 0 && (
                                    <Tag>
                                        +{hiddenCount}
                                    </Tag>
                                )}
                            </Space>
                        </Tooltip>
                    );
                }
            }
        ];
    }, [
        counterpartyOptions,
        editingInvoice,
        saveEditedInvoice,
        cancelEditing,
        gepardMismatchHints
    ]);

    // Объединяем счета родителя и всех дочерних задач
    const combinedInvoices = useMemo(() => {
        const result = { 
            // Явно помечаем родительские строки
            income: invoices.income.map(i => ({ ...i, isChild: false })), 
            expense: invoices.expense.map(i => ({ ...i, isChild: false })) 
        };

        childTasks.forEach(child => {
            if (child.invoices && Array.isArray(child.invoices)) {
                child.invoices.forEach(inv => {
                    result[inv.type].push({
                        ...inv,
                        isChild: true, // Флаг дочерней строки
                        childExternalId: child.external_id 
                    });
                });
            }
        });
        return result;
    }, [invoices, childTasks]);

    return (
        <Layout>
            <div style={{ padding: 24, maxWidth: '100%', margin: '0 auto' }}>
                <h1 style={{ marginBottom: 20 }}>Управление задачами</h1>

                <div style={{ background: '#f5f5f5', padding: 20, marginBottom: 20, borderRadius: 8 }}>
                  
    {/* РЯД 1: Основная информация и Исполнители */}
    <Row gutter={[16, 16]}>
        <Col span={4}>
            <Text type="secondary">Внешний ID:</Text>
            <Input.Search
                placeholder="ID Задачи"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onSearch={() => applyFilters()}
                enterButton
            />
        </Col>
        <Col span={4}>
            <Text type="secondary">Объект (БС):</Text>
            <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder="Выберите объекты"
                value={apiFilters.object_id ? apiFilters.object_id.split(',') : []}
                onChange={v => handleApiFilterChange('object_id', v.join(','))}
                style={{ width: '100%' }}
                options={availableObjects}
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
        </Col>
        <Col span={4}>
            <Text type="secondary">Заказчик:</Text>
            <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder="Выберите регионы"
                value={apiFilters.region ? apiFilters.region.split(',') : []}
                onChange={v => handleApiFilterChange('region', v.join(','))}
                style={{ width: '100%' }}
                options={availableRegions}
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
        </Col>
        <Col span={4}>
            <Text type="secondary">Вид работ:</Text>
            <Select
                mode="multiple"
                allowClear
                placeholder="Выберите виды"
                style={{ width: '100%' }}
                value={apiFilters.news_type_name}
                onChange={v => handleApiFilterChange('news_type_name', v)}
                options={availableTypes}
                showSearch
            />
        </Col>
        <Col span={4}>
            <Text type="secondary">Исполнитель:</Text>
            <Select
                mode="multiple"
                allowClear
                placeholder="Кто назначен"
                value={apiFilters.assigned_user_fio ? apiFilters.assigned_user_fio.split(',') : []}
                onChange={v => handleApiFilterChange('assigned_user_fio', v.join(','))}
                style={{ width: '100%' }}
                options={availableUsers}
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
        </Col>
        <Col span={4}>
            <Text type="secondary">Заполнил ВДР:</Text>
            <Select
                mode="multiple"
                allowClear
                placeholder="Аккаунт"
                value={apiFilters.filling_user ? apiFilters.filling_user.split(',') : []}
                onChange={v => handleApiFilterChange('filling_user', v.join(','))}
                style={{ width: '100%' }}
                options={availableFillingUsers}
                showSearch
            />
        </Col>
    </Row>

    {/* РЯД 2: Статусы и Рентабельность */}
    <Row gutter={[16, 16]} style={{ marginTop: 15 }}>
        <Col span={4}>
            <Text type="secondary">Этапы:</Text>
            <Select mode="multiple" placeholder="Этапы" value={apiFilters.onlyStatus} onChange={v => handleApiFilterChange('onlyStatus', v)} style={{ width: '100%' }} options={stageOptions} />
        </Col>
        <Col span={4}>
            <Text type="secondary">Стадия:</Text>
            <Select placeholder="Стадия" mode="multiple" options={[{ value: 1, label: 'В работе' }, { value: 2, label: 'Завершено' }]} value={apiFilters.stage} onChange={v => handleApiFilterChange('stage', v)} style={{ width: '100%' }} />
        </Col>
        <Col span={4}>
            <Text type="secondary">Статус ВДР:</Text>
            <Select placeholder="ВДР статус" mode="multiple" value={apiFilters.status_invoices} onChange={v => handleApiFilterChange('status_invoices', v)} style={{ width: '100%' }} options={TASK_STATUS_OPTIONS} />
        </Col>
        <Col span={6}>
            <Text type="secondary">Рентабельность (%):</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <InputNumber
                    placeholder="От"
                    value={apiFilters.minProfitability}
                    onChange={v => handleApiFilterChange('minProfitability', v)}
                    style={{ width: '100%' }}
                />
                <span>—</span>
                <InputNumber
                    placeholder="До"
                    value={apiFilters.maxProfitability}
                    onChange={v => handleApiFilterChange('maxProfitability', v)}
                    style={{ width: '100%' }}
                />
            </div>
        </Col>
        <Col span={6} style={{ textAlign: 'right', alignSelf: 'flex-end' }}>
            <Space>
                <Button type="primary" onClick={applyFilters}>Применить</Button>
                <Button danger onClick={resetFilters}>Сбросить</Button>
            </Space>
        </Col>
    </Row>

    {/* РЯД 3: Даты (Таймлайны) */}
    <Row gutter={[16, 16]} style={{ marginTop: 15 }}>
        <Col span={3}>
            <Text type="secondary">Nomer_TT:</Text>
            <Input.Search
                placeholder="Номер ТТ"
                // Используем отдельный фильтр, а не общий searchText
                value={apiFilters.nomer_tt}
                onChange={e => handleApiFilterChange('nomer_tt', e.target.value)}
                onSearch={() => applyFilters()}
                enterButton
            />
        </Col>
        <Col span={4}>
            <Text type="secondary" strong>Дата создания:</Text>
            <RangePicker
                showTime
                value={[apiFilters.geCreated, apiFilters.leCreated]}
                onChange={v => { handleApiFilterChange('geCreated', v ? v[0] : null); handleApiFilterChange('leCreated', v ? v[1] : null); }}
                style={{ width: '100%' }}
            />
        </Col>
        <Col span={4}>
            <Text type="secondary" strong>Покидание сайта:</Text>
            <RangePicker
                showTime
                value={[apiFilters.geExit, apiFilters.leExit]}
                onChange={v => { handleApiFilterChange('geExit', v ? v[0] : null); handleApiFilterChange('leExit', v ? v[1] : null); }}
                style={{ width: '100%' }}
            />
        </Col>
        <Col span={4}>
            <Text type="secondary" strong>Обновление:</Text>
            <RangePicker
                showTime
                value={[apiFilters.geUpdated, apiFilters.leUpdated]}
                onChange={v => { handleApiFilterChange('geUpdated', v ? v[0] : null); handleApiFilterChange('leUpdated', v ? v[1] : null); }}
                style={{ width: '100%' }}
            />
        </Col>
        <Col span={4}>
    <Text type="secondary" strong>
        Дата закрытия:
    </Text>

    <RangePicker
        showTime={{
            format: 'HH:mm',
            defaultValue: [
                dayjs('00:00', 'HH:mm'),
                dayjs('23:59', 'HH:mm')
            ]
        }}
        format="DD.MM.YYYY HH:mm"
        value={[
            apiFilters.geClosed,
            apiFilters.leClosed
        ]}
        onChange={value => {
            let from = value ? value[0] : null;
            let to = value ? value[1] : null;

            /*
             * Если конец диапазона без времени (00:00) —
             * берём весь день включительно.
             */
            if (
                to &&
                to.hour() === 0 &&
                to.minute() === 0 &&
                to.second() === 0
            ) {
                to = to
                    .hour(23)
                    .minute(59)
                    .second(59);
            }

            handleApiFilterChange(
                'geClosed',
                from
            );

            handleApiFilterChange(
                'leClosed',
                to
            );
        }}
        style={{
            width: '100%'
        }}
        placeholder={[
            'Закрыта с',
            'Закрыта по'
        ]}
    />
</Col>
        <Col span={3}>
            <Text type="secondary">Заказ пок.:</Text>
            <Select
                placeholder="Все"
                style={{ width: '100%' }}
                allowClear
                value={apiFilters.has_customer_order}
                onChange={v => handleApiFilterChange('has_customer_order', v)}
                options={[{label:'Да', value:'yes'}, {label:'Нет', value:'no'}]}
            />
        </Col>
        <Col span={3}>
            <Text type="secondary">Заказ пост.:</Text>
            <Select
                placeholder="Все"
                style={{ width: '100%' }}
                allowClear
                value={apiFilters.has_supplier_order}
                onChange={v => handleApiFilterChange('has_supplier_order', v)}
                options={[{label:'Да', value:'yes'}, {label:'Нет', value:'no'}]}
            />
        </Col>
        <Col span={3}>
            <Text type="secondary">Еж. отчет:</Text>
            <Select
                placeholder="Все"
                style={{ width: '100%' }}
                allowClear
                value={apiFilters.has_daily_report}
                onChange={v => handleApiFilterChange('has_daily_report', v)}
                options={[{label:'Да', value:'yes'}, {label:'Нет', value:'no'}]}
            />
        </Col>
        <Col span={3}>
            <Text type="secondary">Расхожд. с почтой:</Text>
            <Select
                placeholder="Все"
                style={{ width: '100%' }}
                allowClear
                value={apiFilters.has_mail_discrepancies}
                onChange={v => handleApiFilterChange('has_mail_discrepancies', v)}
                options={[
                    { label: 'Есть', value: 'yes' },
                    { label: 'Нет', value: 'no' }
                ]}
            />
        </Col>

    </Row>

                </div>

                <Space style={{marginBottom: 20}} wrap>
                    <Button icon={<SyncOutlined/>} type="primary" onClick={syncTasks}>Синхронизировать</Button>
                    <Button
                        onClick={confirmBulkAutoEstimate}
                        loading={bulkEstimating}
                        disabled={!selectedTasks.length}
                    >
                        Автоосмечивание{selectedTasks.length ? ` (${selectedTasks.length})` : ''}
                    </Button>
                    {selectedTasks.length ? (
                        <Button type="link" onClick={() => setSelectedTasks([])}>Снять выбор</Button>
                    ) : null}
                    <Button
                        icon={<DollarCircleOutlined/>}
                        onClick={openExportTemplateModal}
                        loading={isExporting}
                    >
                        Экспорт ВДР <DownOutlined />
                    </Button>
                </Space>

                <Modal
                    title="Шаблон экспорта ВДР"
                    open={exportTemplateOpen}
                    onCancel={() =>
                        setExportTemplateOpen(false)
                    }
                    onOk={() =>
                        exportAllInvoices(exportTemplate)
                    }
                    okText="Выгрузить"
                    cancelText="Отмена"
                    confirmLoading={isExporting}
                    destroyOnClose
                >
                    <Radio.Group
                        value={exportTemplate}
                        onChange={e =>
                            setExportTemplate(
                                e.target.value
                            )
                        }
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12
                        }}
                    >
                        <Radio value="full">
                            Полный шаблон
                            <Text
                                type="secondary"
                                style={{
                                    display: 'block',
                                    marginLeft: 24
                                }}
                            >
                                Все поля ВДР + кастомные
                                поля задачи
                            </Text>
                        </Radio>
                        <Radio value="short">
                            Сокращённый шаблон
                            <Text
                                type="secondary"
                                style={{
                                    display: 'block',
                                    marginLeft: 24
                                }}
                            >
                                ID, внешний ID, вид работ,
                                исполнитель, номер, объект,
                                описание выполненных работ,
                                статья, расценка, ед., кол-во,
                                цена, стоимость, даты,
                                %, примечание, учитывать
                            </Text>
                        </Radio>
                    </Radio.Group>
                </Modal>

                <Table
                    dataSource={tasks}
                    rowKey="id"
                    loading={loading}
                    rowSelection={{
                        selectedRowKeys: selectedTasks.map(task => task.id),
                        preserveSelectedRowKeys: true,
                        onChange: keys => {
                            const keySet = new Set(keys);
                            setSelectedTasks(prev => {
                                const pageIds = new Set(tasks.map(task => task.id));
                                const kept = prev.filter(task => !pageIds.has(task.id) && keySet.has(task.id));
                                const added = tasks.filter(task => keySet.has(task.id));
                                return [...kept, ...added];
                            });
                        }
                    }}
                    onChange={handleTableChange}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        pageSizeOptions: ['20', '50', '100'],
                        hideOnSinglePage: false,
                        showTotal: (total, range) =>
                            total > 0
                                ? `${range[0]}–${range[1]} из ${total}`
                                : 'Всего: 0'
                    }}
                    scroll={{ x: 1300 }}
                    columns={[
                        { title: 'ID',
                            render: (_, record) => {
                                try {
                                    const rawFields = record.raw_data?.custom_fields;
                                    const customFields = typeof rawFields === 'string' ? JSON.parse(rawFields) : rawFields;
                                    return customFields?.Nomer_TT?.value || record.Nomer_TT || '-';
                                } catch (e) { return record.Nomer_TT || '-'; }
                            }, width: 70, sorter: true },
                        { title: 'Внешний ID', dataIndex: 'external_id', width: 110, sorter: true },
                        { title: 'Название', dataIndex: 'title', ellipsis: true, sorter: true },
                        {
                            title: 'Вид работ',
                            dataIndex: 'news_type_name',
                            width: 150,
                            filters: availableTypes.map(t => ({ text: t.label, value: t.value })),
                            filterSearch: true,
                            // ПРИВЯЗКА К СТАРОМУ ФИЛЬТРУ
                            filteredValue: apiFilters.news_type_name || null,
                        },
                        {
                            title: 'Этап',
                            key: 'onlyStatus', // Ключ для фильтрации
                            dataIndex: ['raw_data', 'status_name'],
                            width: 150,
                            filters: availableStages.map(s => ({ text: s.label, value: s.value })),
                            filterSearch: true,
                            // ПРИВЯЗКА К СТАРОМУ ФИЛЬТРУ
                            filteredValue: apiFilters.onlyStatus || null,
                        },
                        {
                            title: 'Статус ВДР',
                            key: 'status_invoices',
                            dataIndex: 'status_invoices',
                            width: 130,
                            filters: TASK_STATUS_OPTIONS.map(o => ({ text: o.label, value: o.value })),
                            // ПРИВЯЗКА К СТАРОМУ ФИЛЬТРУ
                            filteredValue: Array.isArray(apiFilters.status_invoices) ? apiFilters.status_invoices : (apiFilters.status_invoices ? apiFilters.status_invoices.split(',') : null),
                            render: s => <Tag color={s === 'approved' ? 'green' : 'blue'}>{TASK_STATUS_OPTIONS.find(o => o.value === s)?.label || s}</Tag>
                        },
                        {
                            title: 'Созданы документы',
                            width: 250, // Увеличим ширину для процентов
                            render: (_, r) => {
                                const renderLinks = (rawData, label, basePath = '/docs') => {
                                    if (!rawData) return <div>{label}: <Text type="secondary">-</Text></div>;

                                    const docs = rawData.split('|');
                                    return (
                                        <div style={{ marginBottom: 2 }}>
                                            <Text type="secondary" style={{ fontSize: '11px' }}>{label}: </Text>
                                            {docs.map((str, idx) => {
                                                const [id, num, percent] = str.split(':');

                                                // Цвет процента
                                                let pctColor = '#8c8c8c';
                                                if (percent) {
                                                    const p = parseInt(percent);
                                                    if (p >= 90) pctColor = '#52c41a';
                                                    else if (p < 50) pctColor = '#f5222d';
                                                }

                                                return (
                                                    <span key={id}>
                                <a
                                    href={`${basePath}?id=${id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: '11px', textDecoration: 'underline' }}
                                >
                                    №{num}
                                </a>
                                                        {percent !== undefined && (
                                                            <span style={{ fontSize: '10px', color: pctColor, fontWeight: 'bold', marginLeft: '2px' }}>
                                        ({percent}%)
                                    </span>
                                                        )}
                                                        {idx < docs.length - 1 ? ', ' : ''}
                            </span>
                                                );
                                            })}
                                        </div>
                                    );
                                };

                                return (
                                    <div style={{ lineHeight: '1.4', padding: '4px 0' }}>
                                        {renderLinks(r.linked_customer_orders, 'Заказ пок.', '/docs')}
                                        {renderLinks(r.linked_supplier_orders, 'Заказ пост.', '/docs')}
                                        {renderLinks(r.linked_daily_reports, 'Еж. отчет', '/daily-reports')}
                                    </div>
                                );
                            }
                        },
                        { title: 'Исполнитель', dataIndex: 'assigned_user_fio', width: 180, sorter: true },
                        {
                            title: 'Заказчик',
                            width: 180,
                            render: (_, record) => {
                                try {
                                    const rawFields = record.raw_data?.custom_fields;
                                    const customFields = typeof rawFields === 'string' ? JSON.parse(rawFields) : rawFields;
                                    return customFields?.Zakazchik?.value || record.assigned_department_name || '-';
                                } catch (e) { return record.assigned_department_name || '-'; }
                            }
                        },
                        {
                            title: 'Рент. (%)',
                            key: 'profitability',
                            width: 100,
                            align: 'right',
                            sorter: true,
                            render: (_, r) => {
                                const inc = parseFloat(r.total_income) || 0;
                                const exp = parseFloat(r.total_expense) || 0;
                                if (inc === 0) return '0%';
                                const res = Math.round(((inc - exp) / inc) * 100);
                                return <Text type={res < 20 ? 'danger' : 'success'}>{res}%</Text>;
                            }
                        },
                        { title: 'Дата создания задания', dataIndex: ['raw_data','news_date'], width: 140, sorter: true, render: d => d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '-' },
                        {
                            title: 'Дата покидания сайта',
                            width: 140,
                            render: (_, record) => {
                                try {
                                    const rawFields = record.raw_data?.custom_fields;
                                    const customFields = typeof rawFields === 'string' ? JSON.parse(rawFields) : rawFields;
                                    let val = customFields?.Vremja_pokidanija_sayta?.value;
                                    if (!val || val === 'false') return '-';
                                    const isNumeric = /^\d+$/.test(val.toString());
                                    if (isNumeric) {
                                        const num = val.toString().length === 10 ? Number(val) * 1000 : Number(val);
                                        let date = dayjs(num);
                                        const pcOffset = date.utcOffset();
                                        const mskOffset = 180;
                                        date = date.add(mskOffset - pcOffset, 'minute');
                                        return date.format('DD.MM.YYYY HH:mm');
                                    }
                                    const parsedDate = dayjs(val, "DD.MM.YYYY HH:mm:ss");
                                    if (parsedDate.isValid()) return parsedDate.format('DD.MM.YYYY HH:mm');
                                    return val.toString().substring(0, 16);
                                } catch (e) { return '-'; }
                            }
                        },
                        { title: 'Дата обновления', dataIndex: ['raw_data','update_date'], width: 140, sorter: true, render: d => d ? dayjs(d).format('DD.MM.YYYY HH:mm') : '-' },
                        {
                            title: 'Заполнил ВДР',
                            dataIndex: 'filling_user_fio',
                            width: 180,
                            render: (fio, record) => fio || record.filling_user_name || '-'
                        },
                        { title: 'ВДР', fixed: 'right', width: 80, align: 'center', render: (_, r) => <Button type="primary" shape="circle" icon={<DollarCircleOutlined />} onClick={() => openInvoiceModal(r.id, r.external_id, r.status_invoices)} /> }
                    ]}
                />

                <Modal
                    title={
                        <Space style={{width: '100%', justifyContent: 'space-between'}}>
                            <span>Ведомость доходов и расходов № {dbTaskId} </span>
                            <span>Задача <a
                                href={`https://smartcomservice.geofsm.ru/tasks/#task/${currentTaskId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                            {currentTaskId}
                            </a>  | {externalTaskData?.title}</span>
                            {/* БЛОК ВЫБОРА СЦЕНАРИЯ */}
                            <Space style={{ background: '#fff', padding: '4px 8px', borderRadius: '4px', border: '1px solid #d9d9d9' }}>
                                <Select
                                    placeholder="Выбрать сценарий вручную..."
                                    style={{ width: 250 }}
                                    showSearch
                                    optionFilterProp="label"
                                    onChange={setManualScenarioId}
                                    options={allScenarios.map(s => ({ value: s.id, label: s.scenario_name }))}
                                />
                                <Button 
                                    type="primary" 
                                    icon={<PlusOutlined />} 
                                    onClick={handleApplyManualScenario}
                                    disabled={!manualScenarioId}
                                >
                                    Заполнить
                                </Button>
                            </Space>

                            <Space style={{ padding: '4px 50px 4px 8px', borderRadius: '4px', border: '1px solid #d9d9d9' }}>
                                <Button icon={<SyncOutlined/>} onClick={handleFindAndApplyScenario} loading={scenarioCheckLoading} title="Автоматический поиск сценария">Автосмечивание</Button>
                                <Popconfirm title="Очистить все?" onConfirm={clearAllInvoices}><Button icon={<ClearOutlined/>} danger /></Popconfirm>
                            </Space>
                        </Space>
                    }
                    open={modalIsOpen}
                    onCancel={() => setModalIsOpen(false)}
                    afterOpenChange={(open) => {
                        if (open) {
                            // Принудительно вызываем скролл после открытия анимации модалки
                            // (useEffect выше тоже сработает, но это дополнительная гарантия)
                            scrollToBottom(commentsContainerRef);
                            scrollToBottom(historyContainerRef);
                        }
                    }}
                    width="98%"
                    style={{top: 20}}
                    bodyStyle={{maxHeight: '82vh', overflowY: 'auto'}}
                    footer={[
                        <div key="merged-footer" style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            width: '100%',
                            background: '#f0f7ff', // Сделали фон более легким
                            padding: '10px 20px',
                            borderRadius: '0 0 8px 8px',
                            borderTop: '1px solid #91d5ff'
                        }}>
                            {/* ЛЕВАЯ ЧАСТЬ: ИТОГИ */}
                            <div style={{ fontSize: '18px' }}>
                                <Space size="middle">
                                    <span>Доход: <Text type="success" strong>{combinedInvoices.income.reduce((s, i) => s + (i.include!==false ? (parseFloat(i.amount)||0) : 0), 0).toLocaleString('ru-RU')} р.</Text></span>
                                    <span>Расход: <Text type="danger" strong>{combinedInvoices.expense.reduce((s, i) => s + (i.include!==false ? (parseFloat(i.amount)||0) : 0), 0).toLocaleString('ru-RU')} р.</Text></span>
                                    <span>Маржа: <Text strong>{(combinedInvoices.income.reduce((s, i) => s + (i.include!==false ? (parseFloat(i.amount)||0) : 0), 0) - combinedInvoices.expense.reduce((s, i) => s + (i.include!==false ? (parseFloat(i.amount)||0) : 0), 0)).toLocaleString('ru-RU', {minimumFractionDigits:2})} р.</Text></span>
                                    <Tag color="processing" style={{ fontSize: '14px', padding: '2px 10px' }}>
                                        Рент: {(() => {
                                            const inc = combinedInvoices.income.reduce((s, i) => s + (i.include!==false ? (parseFloat(i.amount)||0) : 0), 0);
                                            const exp = combinedInvoices.expense.reduce((s, i) => s + (i.include!==false ? (parseFloat(i.amount)||0) : 0), 0);
                                            return inc > 0 ? Math.round(((inc - exp) / inc) * 100) : 0;
                                        })()}%
                                    </Tag>
                                </Space>
                            </div>

                            {/* ПРАВАЯ ЧАСТЬ: ДАТА, СТАТУС И КНОПКИ */}
<div
    style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        justifyContent: 'flex-end'
    }}
>
    <span
        style={{
            fontWeight: 'bold'
        }}
    >
        Дата закрытия:
    </span>

    <DatePicker
        showTime={{
            format: 'HH:mm'
        }}
        format="DD.MM.YYYY HH:mm"
        placeholder="Не указана"
        allowClear
        value={taskClosedAt}
        onChange={updateTaskClosedAt}
        disabled={
            taskClosedAtSaving ||
            !dbTaskId
        }
        style={{
            width: 180
        }}
    />

    {taskClosedAtSaving && (
        <SyncOutlined spin />
    )}

    <span
        style={{
            marginLeft: 10,
            fontWeight: 'bold'
        }}
    >
        Статус ВДР:
    </span>

    <Select
        options={
            TASK_STATUS_OPTIONS
        }
        value={
            currentTaskStatus
        }
        onChange={
            updateTaskStatus
        }
        style={{
            width: 140,
            textAlign: 'left'
        }}
    />

    <Space>
        <Button
            onClick={() =>
                setModalIsOpen(false)
            }
        >
            Закрыть
        </Button>

        <Button
            type="primary"
            icon={
                <SaveOutlined />
            }
            onClick={
                saveInvoiceChanges
            }
            loading={
                isSaving
            }
            disabled={
                Boolean(
                    editingInvoice
                )
            }
            danger={
                hasUnsavedChanges
            }
        >
            {hasUnsavedChanges
                ? 'Сохранить изменения *'
                : 'Данные сохранены'}
        </Button>
    </Space>
</div>
                        </div>
                    ]}
                >
                    {invoiceLoading ?
                        <div style={{textAlign: 'center', padding: 50}}><SyncOutlined spin style={{fontSize: 24}}/><br/>Загрузка
                            данных...</div> : (
                            <>
                                {/* ИНДИКАТОР СТАТУСА В ВЕРХНЕЙ ЧАСТИ */}
                                <div style={{ marginBottom: 15 }}>
                                    {gepardCheck ? (
                                        <Alert
                                            message={
                                                gepardCheck.mail_kind ===
                                                'cancel'
                                                    ? 'Gepard: Отмена'
                                                    : 'Gepard: Закрытие'
                                            }
                                            description={
                                                gepardCheck.has_discrepancies
                                                    ? (
                                                        (
                                                            gepardCheck
                                                                .suggestions
                                                                ?.to_add ||
                                                            []
                                                        )
                                                            .length > 0
                                                            ? `Письмо предлагает добавить ${(
                                                                gepardCheck
                                                                    .suggestions
                                                                    ?.to_add ||
                                                                []
                                                            ).length} поз. в доход. Можно нажать «Добавить предложенное», затем сохранить ВДР.`
                                                            : `Есть расхождения. Добавить: ${(
                                                                gepardCheck
                                                                    .suggestions
                                                                    ?.to_add ||
                                                                []
                                                            ).length}, удалить: ${(
                                                                gepardCheck
                                                                    .suggestions
                                                                    ?.to_remove ||
                                                                []
                                                            ).length}. Предложения — внизу окна.`
                                                      )
                                                    : gepardCheck.is_demo
                                                      ? 'Тестовое письмо Gepard. Предложения — внизу окна.'
                                                      : 'ВДР совпал с письмом Gepard.'
                                            }
                                            type={
                                                gepardCheck.has_discrepancies
                                                    ? 'error'
                                                    : gepardCheck.is_demo
                                                      ? 'info'
                                                      : 'success'
                                            }
                                            showIcon
                                            style={{
                                                marginBottom: 10,
                                                ...(
                                                    !gepardCheck.has_discrepancies &&
                                                    !gepardCheck.is_demo
                                                        ? {
                                                            backgroundColor:
                                                                '#f6ffed',
                                                            border:
                                                                '1px solid #b7eb8f'
                                                        }
                                                        : {}
                                                )
                                            }}
                                            action={
                                                <Space>
                                                    {(
                                                        gepardCheck
                                                            .suggestions
                                                            ?.to_add ||
                                                        []
                                                    )
                                                        .length >
                                                    0 ? (
                                                        <Button
                                                            size="small"
                                                            type="primary"
                                                            onClick={
                                                                applyGepardAddSuggestions
                                                            }
                                                        >
                                                            Добавить
                                                            предложенное
                                                        </Button>
                                                    ) : null}
                                                    <Button
                                                        size="small"
                                                        onClick={() =>
                                                            setGepardModalOpen(
                                                                true
                                                            )
                                                        }
                                                    >
                                                        Детали
                                                    </Button>
                                                    {gepardCheck.is_demo ? (
                                                        <Tag color="purple">
                                                            тест
                                                        </Tag>
                                                    ) : null}
                                                </Space>
                                            }
                                        />
                                    ) : null}
                                    {hasUnsavedChanges ? (
                                        <Alert
                                            message="Внесены изменения"
                                            description="У вас есть несохраненные данные в таблицах доходов или расходов. Нажмите кнопку 'Сохранить' внизу окна."
                                            type="warning"
                                            showIcon
                                            action={
                                                <Button size="small" type="primary" onClick={saveInvoiceChanges} loading={isSaving}>
                                                    Сохранить сейчас
                                                </Button>
                                            }
                                        />
                                    ) : (
                                        <Alert
                                            message="Все изменения сохранены"
                                            type="success"
                                            showIcon
                                            style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}
                                        />
                                    )}
                                </div>
                                {externalTaskData && (
                                    console.log(externalTaskData),
                                    <Collapse activeKey={expandedSections} onChange={setExpandedSections}
                                              style={{marginBottom: 15}} ghost>
                                        <Panel header={<b>Основная информация по задаче</b>} key="basic">
                                            <Row gutter={[16, 8]}>
                                                <Col span={6}>
                                                    <div style={{
                                                        padding: '12px',
                                                        border: '1px solid #e8e8e8', // Цвет рамки
                                                        borderRadius: '8px',         // Скругление углов
                                                        background: '#fafafa',       // Легкий серый фон для выделения
                                                        height: '100%',              // Растянуть на всю высоту ряда
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)' // Еле заметная тень
                                                    }}>
                                                        {renderCustomField('Zakazchik', 'Заказчик')}
                                                        {renderField('Исполнитель', externalTaskData.assigned_user_fio)}
                                                        {renderCustomField('FIO_#1', 'Соисполнитель')}
                                                        {renderCustomField('FIO_#3', 'Соисполнитель 2')}
                                                        {renderCustomField('Nomer_TT', 'Номер ТТ')}
                                                        {renderCustomField('Nomer_BS', 'Объект (БС)')}
                                                        {renderParentTask()}
                                                        {renderChildTasks()}
                                                    </div>
                                                </Col>
                                                <Col span={6}>
                                                    <div style={{
                                                        padding: '12px',
                                                        border: '1px solid #e8e8e8', // Цвет рамки
                                                        borderRadius: '8px',         // Скругление углов
                                                        background: '#fafafa',       // Легкий серый фон для выделения
                                                        height: '100%',              // Растянуть на всю высоту ряда
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)' // Еле заметная тень
                                                    }}>
                                                        {renderField('Вид работ', externalTaskData.news_type_name)}
                                                        {renderField('Этап', externalTaskData.status_name)}
                                                        {renderField('Стадия', {
                                                            0: 'Отказано',
                                                            1: 'В работе',
                                                            2: 'Завершено'
                                                        }[externalTaskData.confirmed])}
                                                        {renderCustomField('Adres_BS', 'Адрес')}
                                                    </div>
                                                </Col>
                                                <Col span={6}>
                                                    <div style={{
                                                        padding: '12px',
                                                        border: '1px solid #e8e8e8', // Цвет рамки
                                                        borderRadius: '8px',         // Скругление углов
                                                        background: '#fafafa',       // Легкий серый фон для выделения
                                                        height: '100%',              // Растянуть на всю высоту ряда
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)' // Еле заметная тень
                                                    }}>
                                                        {renderCustomField('Vremja_pojavlenija_na_sayte', 'Прибыл')}
                                                        {renderCustomField('Vremja_pokidanija_sayta', 'Покинул')}
                                                        {renderCustomField('Vremja_na_rabote', 'На объекте')}
                                                        {renderCustomField('CHase_dizelenija', 'Дизеление')}
                                                    </div>
                                                </Col>
                                                <Col span={6}>
                                                    <div style={{
                                                        padding: '12px',
                                                        border: '1px solid #e8e8e8', // Цвет рамки
                                                        borderRadius: '8px',         // Скругление углов
                                                        background: '#fafafa',       // Легкий серый фон для выделения
                                                        height: '100%',              // Растянуть на всю высоту ряда
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)' // Еле заметная тень
                                                    }}>
                                                        {renderCustomField('Doroga_do_obiekta_(minut)', 'Туда (мин)')}
                                                        {renderCustomField('Doroga_s_obiekta_(minut)', 'Обратно (мин)')}
                                                        {renderTotalRoadTime()}
                                                        {renderCustomField('Probeg,_km', 'Пробег (км)')}
                                                        {renderField('Аккаунт заполняющего ВДР', externalTaskData.filling_user_fio || '—')}
                                                    </div>
                                                </Col>
                                                <Col span={24} style={{marginTop: 10}}>
                                                    <Text strong>Задание:</Text>
                                                    <div style={{padding: '8px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, marginTop: 5}}>
                                                        {renderField(externalTaskData.text)}
                                                    </div>
                                                </Col>
                                                <Col span={24}>
                                                    <Row gutter={16}> {/* Row внутри Col span={24} теперь растянется на 100% */}
                                                        <Col span={12} style={{marginTop: 10}}>
                                                            <Text strong>Описание выполненных работ:</Text>
                                                            <div style={{
                                                                padding: '8px',
                                                                background: '#fffbe6',
                                                                border: '1px solid #ffe58f',
                                                                borderRadius: 4,
                                                                marginTop: 5,
                                                                minHeight: '100px'
                                                            }}>
                                                                {getDynamicValue('<Opisanie_vepolnenneh_rabot>', externalTaskData, JSON.parse(externalTaskData.custom_fields || '{}')) || 'Нет данных'}
                                                            </div>
                                                        </Col>
                                                        
                                                        <Col span={12} style={{marginTop: 10}}>
                                                            <Text strong>Комментарии исполнителя (не для заказчика):</Text>
                                                            <div style={{
                                                                padding: '8px',
                                                                background: '#fffbe6',
                                                                border: '1px solid #ffe58f',
                                                                borderRadius: 4,
                                                                marginTop: 5,
                                                                minHeight: '100px'
                                                            }}>
                                                                {getDynamicValue('<comments>', externalTaskData, JSON.parse(externalTaskData.custom_fields || '{}')) || 'Нет данных'}
                                                            </div>
                                                           
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            
                                        </Row>
                                    </Panel>
                                </Collapse>
                            )}

                            {['income', 'expense'].map(type => (
                                <Collapse key={type} activeKey={[type]} onChange={setExpandedSections} style={{marginBottom: 15}}>
                                    <Panel key={type} showArrow={false} 
            // Это свойство отключает возможность сворачивания/разворачивания кликом
            collapsible="disabled"  header={
                                        <Space>
                                            <Popconfirm
                                                title={
                                                    `Удалить все свободные ${
                                                        type === 'income'
                                                            ? 'доходные'
                                                            : 'расходные'
                                                    } строки? Заблокированные строки останутся.`
                                                }
                                                onConfirm={event => {
                                                    event?.stopPropagation();
                                                    handleClearByType(type);
                                                }}
                                                okText="Да"
                                                cancelText="Нет"
                                            >
                                                <Button
                                                    size="small"
                                                    icon={<ClearOutlined/>}
                                                    danger
                                                    onClick={e => e.stopPropagation()}
                                                    disabled={!!editingInvoice || invoices[type].length === 0}
                                                >
                                                    Очистить раздел
                                                </Button>
                                            </Popconfirm>

                                            <Button
                                                size="small"
                                                type="primary"
                                                icon={<PlusOutlined/>}
                                                onClick={(e) => { e.stopPropagation(); handleAddNewInlineInitiate(type); }}
                                                disabled={!!editingInvoice}
                                            >
                                                Добавить строку
                                            </Button>
                                        </Space>
                                    }>
                                        <div
                                            onKeyDown={e => {
                                                if (
                                                    e.key !== 'Tab' ||
                                                    !editingInvoice
                                                ) {
                                                    return;
                                                }
                                                const t = e.target;
                                                const blocked =
                                                    Boolean(
                                                        t?.readOnly ||
                                                            t?.disabled ||
                                                            t?.tabIndex ===
                                                                -1 ||
                                                            t?.closest?.(
                                                                '.ant-select-disabled'
                                                            ) ||
                                                            t?.closest?.(
                                                                '.ant-input-number-readonly'
                                                            ) ||
                                                            t?.closest?.(
                                                                '.ant-input-disabled'
                                                            )
                                                    );
                                                if (!blocked) return;
                                                const moved =
                                                    focusNextEditableInRow(
                                                        t,
                                                        e.shiftKey
                                                    );
                                                if (moved) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }
                                            }}
                                        >
                                        <Form
                                            form={editInvoiceForm}
                                            component={false}
                                        >
                                            <Table
                                                dataSource={combinedInvoices[type]}
                                                columns={invoiceTableColumns(editInvoiceForm, type)}
                                                pagination={false}
                                                rowKey="id"
                                                scroll={{x: 1500}}
                                                bordered
                                                size="small"
                                                rowClassName={record => {
                                                    const classes = [];

                                                    if (record.isChild) {
                                                        classes.push(
                                                            'task-invoice-child-row'
                                                        );
                                                    }

                                                    if (
                                                        isInvoiceLocked(record)
                                                    ) {
                                                        classes.push(
                                                            'task-invoice-locked-row'
                                                        );
                                                    }

                                                    if (
                                                        type === 'income' &&
                                                        gepardMismatchCodes.has(
                                                            extractWorkCodeFromRate(
                                                                record.rate_name
                                                            )
                                                        )
                                                    ) {
                                                        classes.push(
                                                            'task-invoice-gepard-mismatch'
                                                        );
                                                    }

                                                    return classes.join(' ');
                                                }}
                                                onRow={(record) => ({
                                                    onDoubleClick: () => {
                                                        if (record.isChild) {
                                                            message.info(`Это запись из дочерней задачи #${record.childExternalId}. Она только для чтения.`);
                                                            return;
                                                        }
                                                        if (isInvoiceLocked(record)) {
                                                            message.warning(
                                                                `Редактирование запрещено. Строка находится в документе: ${
                                                                    record
                                                                        .linked_registry_numbers ||
                                                                    'не указан'
                                                                }`
                                                            );

                                                            return;
                                                        }
                                                        if (!editingInvoice) {
                                                            startEditingInvoice(record, type);
                                                        } else if (editingInvoice.id !== record.id) {
                                                            message.warning('Завершите редактирование текущей строки');
                                                        }
                                                    },
                                                })}
                                                summary={pageData => {
                                                    const total = pageData.reduce((sum, curr) => {
                                                        if (curr.include === false) return sum;
                                                        // Теперь просто суммируем amount, так как штраф там уже учтен
                                                        return sum + (parseFloat(curr.amount) || 0);
                                                    }, 0);
                                                    return (
                                                        <Table.Summary.Row style={{background: '#fafafa'}}>
                                                            <Table.Summary.Cell index={0} colSpan={7} align="right"><strong>Итого по разделу:</strong></Table.Summary.Cell>
                                                            <Table.Summary.Cell index={1} align="right"><strong>{mathRound(total).toLocaleString('ru-RU', {minimumFractionDigits:2})}</strong></Table.Summary.Cell>
                                                            <Table.Summary.Cell index={2} colSpan={4} />
                                                        </Table.Summary.Row>
                                                    );
                                                }}
                                            />
                                        </Form>
                                        </div>
                                    </Panel>
                                </Collapse>
                            ))}

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Collapse activeKey={expandedSections} onChange={setExpandedSections}>
                                        <Panel header={<h3>Комментарии к расчету</h3>} key="comments" forceRender >
                                            {/* Контейнер с автоскроллом */}
                                            <div 
                                                ref={commentsContainerRef}
                                                style={{ 
                                                    height: '200px', // Фиксированная высота для скролла
                                                    overflowY: 'auto', 
                                                    marginBottom: '10px', 
                                                    background: '#f9f9f9', 
                                                    padding: '8px', 
                                                    borderRadius: '4px',
                                                    border: '1px solid #f0f0f0'
                                                }}
                                            >
                                                {commentHistory.length > 0 ? (
                                                    // Сортируем: старые вверху, новые внизу
                                                    [...commentHistory].sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt)).map((c, i) => (
                                                        <div key={i} style={{ fontSize: '12px', borderBottom: '1px solid #eee', padding: '6px 0' }}>
                                                            <Text type="secondary" style={{fontSize: '10px'}}>{dayjs(c.createdAt).format('DD.MM.YY HH:mm')}</Text> 
                                                            <br />
                                                            <b>{c.user_name}:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{c.text}</span>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <Text type="secondary">История комментариев пуста</Text>
                                                )}
                                            </div>
                                            
                                            <TextArea 
                                                rows={3} 
                                                value={taskComments} 
                                                onChange={e => {
                                                    setTaskComments(e.target.value);
                                                    setHasUnsavedChanges(true);
                                                }} 
                                                placeholder="Введите новый комментарий..." 
                                            />
                                        </Panel>
                                    </Collapse>
                                </Col>
                                <Col span={12}>
                                    <Collapse activeKey={expandedSections} onChange={setExpandedSections}>
                                        <Panel header={<h3><HistoryOutlined /> История изменений</h3>} key="history" forceRender >
                                            <div 
                                                ref={historyContainerRef}
                                                style={{
                                                    height: '200px', // Увеличил высоту для удобства
                                                    overflowY: 'auto',
                                                    background: '#fff',
                                                    padding: '8px',
                                                    border: '1px solid #f0f0f0',
                                                    borderRadius: '4px'
                                                }}
                                            >
                                                {taskHistory.length > 0 ? (
                                                    // Сортируем: старые вверху, новые внизу
                                                    [...taskHistory].sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt)).map((h, i) => (
                                                        <div key={i} style={{fontSize: '12px', borderBottom: '1px solid #f0f0f0', padding: '4px 0'}}>
                                                            <Text type="secondary" style={{fontSize: '10px'}}>{dayjs(h.createdAt).format('DD.MM HH:mm')}</Text> 
                                                            <br />
                                                            <b>{h.user_name}:</b> {h.action_text}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <Text type="secondary">История пуста</Text>
                                                )}
                                            </div>
                                        </Panel>
                                    </Collapse>
                                </Col>
                            </Row>

                            <InvoiceLinkHistoryPanel
                                taskId={dbTaskId}
                                reloadKey={invoiceLinkHistoryKey}
                            />

                            {(
                                gepardCheck?.has_discrepancies ||
                                gepardCheck?.is_demo
                            ) ? (
                            <div
                                style={{
                                    marginTop: 16,
                                    marginBottom: 8,
                                    padding: 12,
                                    border:
                                        '1px solid #d9d9d9',
                                    borderRadius: 8,
                                    background:
                                        '#fafafa'
                                }}
                            >
                                <Space
                                    style={{
                                        width: '100%',
                                        justifyContent:
                                            'space-between',
                                        marginBottom: 8
                                    }}
                                >
                                    <Text strong>
                                        Gepard → предложения по доходной части
                                        {` (${
                                            gepardCheck.mail_kind ===
                                            'cancel'
                                                ? 'Отмена'
                                                : 'Закрытие'
                                        }, ТТ ${
                                            gepardCheck.tt_number ||
                                            '—'
                                        })`}
                                    </Text>
                                    <Space>
                                        {(
                                            gepardCheck
                                                .suggestions
                                                ?.to_add ||
                                            []
                                        ).length >
                                        0 ? (
                                            <Button
                                                size="small"
                                                type="primary"
                                                onClick={
                                                    applyGepardAddSuggestions
                                                }
                                            >
                                                Добавить предложенное
                                                в ВДР
                                            </Button>
                                        ) : null}
                                        <Button
                                            size="small"
                                            onClick={() =>
                                                setGepardCheck(
                                                    null
                                                )
                                            }
                                        >
                                            Скрыть
                                        </Button>
                                    </Space>
                                </Space>

                                        {gepardCheck.is_demo ? (
                                            <Tag
                                                color="purple"
                                                style={{
                                                    marginBottom: 8
                                                }}
                                            >
                                                тестовые данные
                                            </Tag>
                                        ) : null}

                                        <Text type="secondary">
                                            Что предлагает письмо:
                                        </Text>
                                        <Table
                                            size="small"
                                            pagination={false}
                                            style={{
                                                marginTop: 6,
                                                marginBottom: 12
                                            }}
                                            rowKey={(
                                                _,
                                                index
                                            ) =>
                                                `ew-${index}`
                                            }
                                            rowClassName={row => {
                                                const code =
                                                    normalizeWorkCode(
                                                        row.code
                                                    );

                                                return gepardMissingInVdrCodes.has(
                                                    code
                                                )
                                                    ? 'task-invoice-gepard-missing-vdr'
                                                    : '';
                                            }}
                                            dataSource={
                                                gepardCheck.email_works ||
                                                []
                                            }
                                            columns={[
                                                {
                                                    title: 'Код',
                                                    dataIndex:
                                                        'code',
                                                    width: 90
                                                },
                                                {
                                                    title: 'Наименование',
                                                    dataIndex:
                                                        'name',
                                                    render:
                                                        (
                                                            name,
                                                            row
                                                        ) => {
                                                            const code =
                                                                normalizeWorkCode(
                                                                    row.code
                                                                );
                                                            const missing =
                                                                gepardMissingInVdrCodes.has(
                                                                    code
                                                                );

                                                            return (
                                                                <div>
                                                                    <div>
                                                                        {name}
                                                                    </div>
                                                                    {missing ? (
                                                                        <div
                                                                            style={{
                                                                                marginTop: 4,
                                                                                fontSize: 11,
                                                                                color: '#cf1322'
                                                                            }}
                                                                        >
                                                                            Отсутствует
                                                                            в
                                                                            ВДР
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            );
                                                        }
                                                },
                                                {
                                                    title: 'Кол-во',
                                                    dataIndex:
                                                        'quantity',
                                                    width: 90
                                                }
                                            ]}
                                        />

                                        <Row gutter={12}>
                                            <Col span={8}>
                                                <Alert
                                                    type="success"
                                                    showIcon
                                                    message={`Добавить (${
                                                        (
                                                            gepardCheck
                                                                .suggestions
                                                                ?.to_add ||
                                                            []
                                                        )
                                                            .length
                                                    })`}
                                                    style={{
                                                        marginBottom: 8
                                                    }}
                                                />
                                                <Table
                                                    size="small"
                                                    pagination={false}
                                                    rowKey={(
                                                        _,
                                                        i
                                                    ) =>
                                                        `add-${i}`
                                                    }
                                                    dataSource={
                                                        gepardCheck
                                                            .suggestions
                                                            ?.to_add ||
                                                        []
                                                    }
                                                    locale={{
                                                        emptyText:
                                                            '—'
                                                    }}
                                                    columns={[
                                                        {
                                                            title: 'Код',
                                                            dataIndex:
                                                                'code',
                                                            width: 80
                                                        },
                                                        {
                                                            title: 'Наименование',
                                                            ellipsis:
                                                                true,
                                                            render:
                                                                (
                                                                    _,
                                                                    row
                                                                ) =>
                                                                    row
                                                                        .email
                                                                        ?.name ||
                                                                    row.message ||
                                                                    '—'
                                                        },
                                                        {
                                                            title: 'Кол-во',
                                                            width: 70,
                                                            render:
                                                                (
                                                                    _,
                                                                    row
                                                                ) =>
                                                                    row
                                                                        .email
                                                                        ?.effective_quantity ??
                                                                    row
                                                                        .email
                                                                        ?.quantity ??
                                                                    '—'
                                                        }
                                                    ]}
                                                />
                                            </Col>
                                            <Col span={8}>
                                                <Alert
                                                    type="error"
                                                    showIcon
                                                    message={`Удалить (${
                                                        (
                                                            gepardCheck
                                                                .suggestions
                                                                ?.to_remove ||
                                                            []
                                                        )
                                                            .length
                                                    })`}
                                                    style={{
                                                        marginBottom: 8
                                                    }}
                                                />
                                                <Table
                                                    size="small"
                                                    pagination={false}
                                                    rowKey={(
                                                        _,
                                                        i
                                                    ) =>
                                                        `rm-${i}`
                                                    }
                                                    dataSource={
                                                        gepardCheck
                                                            .suggestions
                                                            ?.to_remove ||
                                                        []
                                                    }
                                                    locale={{
                                                        emptyText:
                                                            '—'
                                                    }}
                                                    columns={[
                                                        {
                                                            title: 'Код',
                                                            dataIndex:
                                                                'code',
                                                            width: 80
                                                        },
                                                        {
                                                            title: 'Кол-во',
                                                            width: 70,
                                                            render:
                                                                (
                                                                    _,
                                                                    row
                                                                ) =>
                                                                    row
                                                                        .vdr
                                                                        ?.quantity ??
                                                                    '—'
                                                        }
                                                    ]}
                                                />
                                            </Col>
                                            <Col span={8}>
                                                <Alert
                                                    type="warning"
                                                    showIcon
                                                    message={`Изменить (${
                                                        (
                                                            gepardCheck
                                                                .suggestions
                                                                ?.to_change ||
                                                            []
                                                        )
                                                            .length
                                                    })`}
                                                    style={{
                                                        marginBottom: 8
                                                    }}
                                                />
                                                <Table
                                                    size="small"
                                                    pagination={false}
                                                    rowKey={(
                                                        _,
                                                        i
                                                    ) =>
                                                        `ch-${i}`
                                                    }
                                                    dataSource={
                                                        gepardCheck
                                                            .suggestions
                                                            ?.to_change ||
                                                        []
                                                    }
                                                    locale={{
                                                        emptyText:
                                                            '—'
                                                    }}
                                                    columns={[
                                                        {
                                                            title: 'Код',
                                                            dataIndex:
                                                                'code',
                                                            width: 80
                                                        },
                                                        {
                                                            title: 'Описание',
                                                            dataIndex:
                                                                'message',
                                                            ellipsis:
                                                                true
                                                        }
                                                    ]}
                                                />
                                            </Col>
                                        </Row>
                            </div>
                            ) : null}

                            
                        </>
                    )}
                </Modal>

                <Modal
                    open={gepardModalOpen}
                    title={
                        gepardCheck
                            ? `Расхождения Gepard · заявка №${
                                gepardCheck.external_id ||
                                gepardCheck.tt_number ||
                                ''
                            }`
                            : 'Расхождения Gepard'
                    }
                    width={900}
                    onCancel={() =>
                        setGepardModalOpen(false)
                    }
                    footer={[
                        <Button
                            key="close"
                            onClick={() =>
                                setGepardModalOpen(false)
                            }
                        >
                            Закрыть
                        </Button>,
                        <Button
                            key="vdr"
                            type="primary"
                            disabled={!gepardCheck?.task_id}
                            onClick={() => {
                                if (
                                    !gepardCheck?.task_id
                                ) {
                                    return;
                                }

                                openInvoiceModal(
                                    gepardCheck.task_id,
                                    gepardCheck.external_id,
                                    currentTaskStatus
                                );
                            }}
                        >
                            Открыть ВДР
                        </Button>
                    ]}
                >
                    {gepardCheck ? (
                        <>
                            <Alert
                                type={
                                    gepardCheck.has_discrepancies
                                        ? 'error'
                                        : 'success'
                                }
                                showIcon
                                style={{
                                    marginBottom: 12
                                }}
                                message={
                                    gepardCheck.has_discrepancies
                                        ? 'Найдены расхождения доходной части с письмом закрытия'
                                        : 'Расхождений нет'
                                }
                                description={
                                    `БС: ${
                                        gepardCheck.bs_number ||
                                        '—'
                                    }; штраф в письме: ${
                                        Number(
                                            gepardCheck.penalty_percent ||
                                            0
                                        )
                                    }%`
                                }
                            />

                            <Table
                                size="small"
                                pagination={false}
                                rowKey={(
                                    _,
                                    index
                                ) =>
                                    `${index}`
                                }
                                dataSource={
                                    gepardCheck.discrepancies ||
                                    []
                                }
                                columns={[
                                    {
                                        title: 'Тип',
                                        dataIndex:
                                            'type',
                                        width: 150,
                                        render:
                                            value => (
                                                <Tag
                                                    color={
                                                        value ===
                                                        'quantity_mismatch' ||
                                                        value ===
                                                        'missing_in_vdr' ||
                                                        value ===
                                                        'missing_in_email' ||
                                                        value ===
                                                        'penalty_mismatch'
                                                            ? 'red'
                                                            : 'orange'
                                                    }
                                                >
                                                    {value}
                                                </Tag>
                                            )
                                    },
                                    {
                                        title: 'Код',
                                        dataIndex:
                                            'code',
                                        width: 90
                                    },
                                    {
                                        title: 'Описание',
                                        dataIndex:
                                            'message'
                                    },
                                    {
                                        title: 'Подсказка',
                                        dataIndex:
                                            'hint',
                                        render:
                                            (
                                                value,
                                                row
                                            ) =>
                                                value ||
                                                (
                                                    row.type ===
                                                    'missing_in_email'
                                                        ? 'Отсутствует в письме'
                                                        : '—'
                                                )
                                    },
                                    {
                                        title: 'Письмо',
                                        width: 110,
                                        render:
                                            (_, row) =>
                                                row.email
                                                    ?.quantity !=
                                                null
                                                    ? row
                                                        .email
                                                        .quantity
                                                    : row
                                                        .email
                                                        ?.penalty_percent !=
                                                    null
                                                        ? `${row.email.penalty_percent}%`
                                                        : row.type ===
                                                          'missing_in_email'
                                                            ? 'нет'
                                                            : '—'
                                    },
                                    {
                                        title: 'ВДР',
                                        width: 110,
                                        render:
                                            (_, row) =>
                                                row.vdr
                                                    ?.quantity !=
                                                null
                                                    ? row
                                                        .vdr
                                                        .quantity
                                                    : row
                                                        .vdr
                                                        ?.penalty !=
                                                    null
                                                        ? `${row.vdr.penalty}%`
                                                        : row.type ===
                                                          'missing_in_vdr'
                                                            ? 'нет'
                                                            : '—'
                                    }
                                ]}
                            />
                        </>
                    ) : null}
                </Modal>

                <Modal
                    title="Выбор сценария для автозаполнения"
                    open={scenarioModalIsOpen}
                    onCancel={() => setScenarioModalIsOpen(false)}
                    footer={null}
                >
                    <div style={{padding: '10px 0'}}>
                        {matchingScenarios.map(s => (
                            <Button key={s.id} block type="default" onClick={() => applySelectedScenario(s)} style={{marginBottom: 10, textAlign: 'left', height: 'auto', padding: '10px 15px'}}>
                                <Text strong>{s.scenario_name}</Text>
                            </Button>
                        ))}
                    </div>
                </Modal>
            </div>
            <style>{`
                .child-row-styling {
                    background-color: #fafafa; /* Светло-серый фон */
                }
                .child-row-styling td {
                    color: #8c8c8c; /* Серый текст */
                }
                .child-row-styling .ant-typography-success, 
                .child-row-styling .ant-typography-danger {
                    opacity: 0.7; /* Приглушаем цвета сумм */
                }
                .task-invoice-locked-row > td {
                    background: #fff1f0 !important;
                }

                .task-invoice-locked-row:hover > td {
                    background: #ffccc7 !important;
                }

                .task-invoice-child-row > td {
                    background: #f0f5ff !important;
                }

                .task-invoice-gepard-mismatch > td {
                    background: #fff2f0 !important;
                }

                .task-invoice-gepard-mismatch:hover > td {
                    background: #ffccc7 !important;
                }

                .task-invoice-gepard-missing-vdr > td {
                    background: #fff7e6 !important;
                }

                .task-invoice-gepard-missing-vdr:hover > td {
                    background: #ffe7ba !important;
                }
            `}</style>
        </Layout>
        
    );
};

export default TasksPage;